import { useState, useEffect, useCallback, Fragment } from 'react';
import { formatDecimal } from '../../lib/utils';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Save, X, Copy, Mail, Download, Plus, Trash2, ChevronDown, ChevronRight,
  FileText, Table2, Printer, GripVertical, Truck, Wallet, MessageSquare, BarChart3, Calculator,
  PieChart as PieChartIcon, ExternalLink, MapPin, Upload, Eye, Paperclip, Pencil,
} from 'lucide-react';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';
import { badgeFor } from '../compras/NodeCostTree';
import { groupByChapter, SENSE_CHAPTER } from '../compras/nodeUtils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DocumentHeader, { HeaderBadge, HeaderLinkBadge } from '../../components/DocumentHeader';
import StatusSelect from '../../components/StatusSelect';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

// ─── Tipus ───────────────────────────────────────────────────────────────────

interface Client { id: string; name: string; code: string; cif?: string; idiomaPressupostId?: number; }
interface Contact { id: string; firstName: string; lastName: string; email?: string; isPrimary?: boolean; }
interface ClientAddressOption { id: string; label: string; address: string; gpsCoordinates?: string; notes?: string; isPrimary?: boolean; }
interface Brand { id: string; code: string; name: string; }
interface BankAccount { id: string; iban: string; bankName: string | null; isDefault: number; }

interface UnitType { code: string; name: string; description: string; }

// "Comanda Client" — documents que el client envia un cop acceptat el
// pressupost (acceptació, comanda de compra, etc.)
interface BudgetDocumentRow { id: string; name: string; fileName: string; fileSize: number; mimeType?: string; createdAt: string; description?: string; }

interface BudgetLine {
  id?: string;
  ordre: number;
  concepte: string;
  descripcioLlarga?: string;
  unitat: string;
  llarg: number;
  ample: number;
  alt: number;
  medicio: number;
  amidament: number;
  tipus: string;
  nodeId?: string;
  nodeName?: string;
  costMaterials: number;
  costServeis: number;
  costIndirecte: number;
  horesP: number;
  horesF: number;
  horesM: number;
  horesO: number;
  horesE: number;
  totalHores: number;
  totalCost: number;
  preuUA: number;
  preuUT: number;
  descompte: number;
  import: number;
  pes: number;
  factor: number;
  escandallLines?: NodeDetailLine[];
  // Amidament propi de l'escandall vinculat en el moment d'importar-lo — es
  // guarda per poder recalcular Preu/U i Cost/UA (Valors/Ut) cada vegada que
  // s'edita el desglose (escandallLines), sense tornar a consultar l'escandall.
  escandallAmidament?: number;
}

interface BudgetForm {
  ecId: string;
  budgetDate: string;
  validityDate: string;
  estat: string;
  idm: string;
  brandId: string | null;
  clientId: string;
  contactId: string;
  projecte: string;
  entradeta: string;
  nostraReferencia: string;
  versio: string;
  amidament: number;
  unitatAmidament: string;
  lliuramentPrevist: string;
  condicioLliurament: string;
  llocLliurament: string;
  ports: string;
  sistPagament: string;
  venciment: string;
  numCompte: string;
  notes: string;
  observacions: string;
  descomptePercent: number;
  ivaPercent: number;
  horaTaller: number;
  horaMuntatge: number;
  horaOficina: number;
  horaExtern: number;
  horaFabricacio: number;
  lines: BudgetLine[];
}

interface Node { id: string; name: string; code?: string; cachedTotalMaterialsCost: number; cachedTotalHoursOwn: number; cachedTotalHoursSubcontract: number; cachedTotalCost: number; cachedTotalWeightKg: number; preuVenda?: number; coefVenda?: number; }

// Còpia editable ("snapshot") de les línies de composició de l'escandall
// vinculat a una línia de pressupost — es crea a partir de GET /nodes/:id
// (mateixa forma que fa servir NodeForm.tsx per a la seva taula "Detall")
// pero viu i s'edita nomes dins d'aquesta línia de pressupost, sense afectar
// mai l'escandall original ni cap altre pressupost que el faci servir.
interface NodeDetailLine {
  childType: 'MATERIAL' | 'NODE';
  description: string;
  nature: string;
  unit: string;
  chapter: string;
  referencia: string;
  detail: string;
  quantity: number;
  dimensionA: number;
  dimensionB: number;
  dimensionH: number;
  isPriceLocked: boolean;
  resolvedUnitCost: number;
  marge: number;
  descompte: number;
  pesTotal: number;
  // Pes unitari (kg/unitat de mesura) del material/escandall vinculat — es fa
  // servir nomes per recalcular pesTotal quan canvia Quantitat/A/B/H (vegeu
  // updateDetailLine); 0 en línies de text lliure, que no en tenen.
  weightKgPerUnit?: number;
  observations: string;
  elementImageUrl?: string | null;
  // Nomes quan childType='NODE': hores totals (pròpies+subcontract) de l'escandall
  // fill, per poder-les sumar al Total Hores de la línia que l'inclou (vegeu
  // recalcHeaderFromDetail) — sense això, un fill NODE nomes aportava cost, mai hores.
  nodeHoresP?: number;
}
interface CompositeCodeOption { id: number; code: string; name: string; marginPercentage?: number | null; }

// Cercador d'elements per importar-los dins del desglose local d'una línia
// (mateix mecanisme que l'autocomplete de "Element" a NodeForm.tsx).
interface SimpleMaterial { id: string; code: string; name: string; unit: string; costPerUnit: number; weightKgPerUnit: number; elementTypeId?: number | null; compositeCodeId?: number | null; pricePerMagnitude?: number; technicalImageUrl?: string | null; }
interface ElementTypeOption { id: number; code: string; name: string; }
const DETAIL_NATURE_KEYWORDS: { pattern: RegExp; nature: string }[] = [
  { pattern: /subcontract/i,                   nature: 'MO_SUBCONTRACT' },
  { pattern: /treball|obra|personal/i,          nature: 'MO_PROPIA' },
  { pattern: /servei|transport|log[ií]stic/i,   nature: 'SERVEIS' },
];
function natureForElementType(et: ElementTypeOption | undefined): string | undefined {
  if (!et) return undefined;
  const text = `${et.code} ${et.name}`;
  return DETAIL_NATURE_KEYWORDS.find(({ pattern }) => pattern.test(text))?.nature;
}

function calcNodeDetailMeasurement(l: NodeDetailLine): number {
  const qty = Number(l.quantity) || 0;
  const a = Number(l.dimensionA) || 0;
  const b = Number(l.dimensionB) || 0;
  const h = Number(l.dimensionH) || 0;
  if (a > 0 && b > 0 && h > 0) return qty * a * b * h;
  if (a > 0 && b > 0)          return qty * a * b;
  if (a > 0)                   return qty * a;
  return qty;
}

// Arrodoneix a `decimals` decimals — evita que errors de coma flotant en
// cadena (sumes/multiplicacions repetides) es mostrin sencers quan l'usuari
// fa focus a una cel·la editable (numCellInput/detailNumCellInput posen el
// número cru, sense format, mentre s'edita).
const round = (v: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round((Number(v) || 0) * factor) / factor;
};

// Recalcula el Cost/UA i Preu/UA de la línia de pressupost a partir del seu
// desglose local (escandallLines) — a diferencia de l'escandall real (que
// divideix per l'Amidament propi per donar un preu "per unitat de mesura",
// com €/m2), aquí Preu/UA es el total de l'escandall sencer (Medició per
// defecte val 1 en vincular-lo): l'usuari fa servir Medició per indicar
// quantes vegades es repeteix aquesta línia sencera, no per desfer cap
// divisió. Es crida cada vegada que l'usuari edita, afegeix o elimina una
// línia del desglose, per aixì mantenir Preu/U (i per tant Import) sempre
// coherent amb el detall.
function recalcHeaderFromDetail(detailLines: NodeDetailLine[], _nodeAmidament: number) {
  let sumImport = 0;
  let sumCostTotal = 0;
  let sumPes = 0;
  let costMaterials = 0;
  let costServeis = 0;
  const hours: Record<'horesO' | 'horesF' | 'horesP' | 'horesM' | 'horesE', number> = {
    horesO: 0, horesF: 0, horesP: 0, horesM: 0, horesE: 0,
  };
  detailLines.forEach((nl) => {
    const measurement = calcNodeDetailMeasurement(nl);
    const costU = Number(nl.resolvedUnitCost || 0);
    const marge = Number(nl.marge || 0);
    const descompte = Number(nl.descompte || 0);
    const preu = costU * marge * (1 - descompte / 100);
    const lineCostTotal = measurement * costU;
    sumImport     += measurement * preu;
    sumCostTotal  += lineCostTotal;
    sumPes        += Number(nl.pesTotal || 0);
    // Mateixa categorització que "tipusCategoryOf" de NodeForm.tsx: els nodes
    // fills (escandalls niats) i les línies de nature MATERIAL compten com a
    // Cost Materials; SERVEIS com a Cost Serveis; MO_PROPIA/MO_SUBCONTRACT
    // repArteixen les seves hores (mesura) entre H.O/H.F/H.P/H.M/H.E per
    // descripció, igual que en vincular l'escandall.
    if (nl.childType === 'NODE') {
      costMaterials += lineCostTotal;
      hours.horesP += measurement * Number(nl.nodeHoresP || 0);
    } else if (nl.nature === 'MATERIAL') {
      costMaterials += lineCostTotal;
    } else if (nl.nature === 'SERVEIS') {
      costServeis += lineCostTotal;
    } else if (nl.nature === 'MO_PROPIA' || nl.nature === 'MO_SUBCONTRACT') {
      hours[laborHourField(nl.description || '')] += measurement;
    }
  });
  return {
    totalCost:     round(sumCostTotal),
    preuUA:        round(sumImport),
    pes:           round(sumPes, 3),
    costMaterials: round(costMaterials),
    costServeis:   round(costServeis),
    horesO: round(hours.horesO),
    horesF: round(hours.horesF),
    horesP: round(hours.horesP),
    horesM: round(hours.horesM),
    horesE: round(hours.horesE),
  };
}

// Recalcula Cost/UA, Preu/U, Pes, Hores i Cost Mat./Serveis d'una línia a
// partir del seu desglose local (escandallLines), si en te — es fa servir
// tant en carregar un pressupost ja desat (perquè les xifres es vegin
// sempre coherents amb el desglose, encara que s'hagi editat en una sessió
// anterior) com en editar el desglose en viu.
function mergeEscandallDerived(line: BudgetLine): BudgetLine {
  if (!line.escandallLines || line.escandallLines.length === 0) return line;
  const amid = line.escandallAmidament ?? 1;
  const derived = recalcHeaderFromDetail(line.escandallLines, amid);
  const prevCostMaterials = Number(line.costMaterials || 0);
  const prevCostIndirecte = Number(line.costIndirecte || 0);
  const indirectRatio = prevCostMaterials > 0 ? prevCostIndirecte / prevCostMaterials : 0;
  return { ...line, ...derived, costIndirecte: derived.costMaterials * indirectRatio };
}

// Mateixa categorització que fa servir la pantalla d'Escandalls (NodeForm.tsx,
// LABOR_CATEGORY_KEYWORDS) per agrupar les línies de mà d'obra (MO_PROPIA/
// MO_SUBCONTRACT) per descripció — aquí es reutilitza per repartir les hores
// entre H.O/H.F/H.P/H.M/H.E en vincular un escandall a una línia.
const LABOR_HOUR_FIELD_KEYWORDS: { pattern: RegExp; field: 'horesO' | 'horesF' | 'horesP' | 'horesM' | 'horesE' }[] = [
  { pattern: /oficina/i,       field: 'horesO' },
  { pattern: /extern/i,        field: 'horesE' },
  { pattern: /muntatge/i,      field: 'horesM' },
  { pattern: /fabricaci[oó]/i, field: 'horesF' },
  { pattern: /producci[oó]/i,  field: 'horesP' },
];
function laborHourField(description: string): 'horesO' | 'horesF' | 'horesP' | 'horesM' | 'horesE' {
  return LABOR_HOUR_FIELD_KEYWORDS.find(({ pattern }) => pattern.test(description))?.field ?? 'horesP';
}


const EMPTY_LINE = (): BudgetLine => ({
  ordre: 0, concepte: '', unitat: 'Ut',
  llarg: 0, ample: 0, alt: 0, medicio: 0, amidament: 1,
  tipus: 'Ut', costMaterials: 0, costServeis: 0, costIndirecte: 0,
  horesP: 0, horesF: 0, horesM: 0, horesO: 0, horesE: 0, totalHores: 0, totalCost: 0,
  preuUA: 0, preuUT: 0, descompte: 0, import: 0, pes: 0, factor: 1,
});

const EMPTY_FORM = (): BudgetForm => ({
  ecId: '',
  budgetDate: new Date().toISOString().split('T')[0],
  validityDate: '', estat: 'ESBORRANY', idm: 'CA',
  brandId: null,
  clientId: '', contactId: '', projecte: '', entradeta: '',
  nostraReferencia: '', versio: 'ESCANDALLS',
  amidament: 1, unitatAmidament: 'Ut',
  lliuramentPrevist: '', condicioLliurament: '', llocLliurament: '',
  ports: '', sistPagament: '', venciment: '', numCompte: '',
  notes: '', observacions: '',
  descomptePercent: 0, ivaPercent: 21, horaTaller: 22.11, horaMuntatge: 26.67, horaOficina: 22.11, horaExtern: 22.11, horaFabricacio: 22.11,
  lines: [],
});

// ─── Formatadors ─────────────────────────────────────────────────────────────

const fmt2 = (v: number) => Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt3 = (v: number | undefined) => Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtCur = (v: number) => Number(v || 0).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' });

// ─── Exportar (Excel/CSV) i Imprimir (PDF via diàleg d'impressió del navegador) —
// mateix patró que Escandalls (NodeForm.tsx): totes les línies de desglose de
// TOTES les línies de pressupost s'aplanen en un únic llistat i s'agrupen per
// Capítol (Cap.), igual que fa groupByChapter/detallByChapterGroups a
// NodeForm.tsx, en lloc d'agrupar-les per línia de pressupost ─────────────────

const DESGLOSE_HEADERS = ['#','Cap.','Ref. Plànol','Descripció','Operació','Element','Foto','Tipus','UA','UN','A','B','C','Amidament','Bloq.','Cost/U','Cost total','Marge','Descompte','Preu','Import','Pes'];
// Índex (0-based) de la columna "Foto" a DESGLOSE_HEADERS/nodeDetailLineToCells
// — usat per exportBudgetDetallToExcel per substituir-la per una imatge.
const DESGLOSE_FOTO_COL_INDEX = 6;
const SENSE_CAPITOL = 'Sense capítol';

function nodeDetailLineToCells(nl: NodeDetailLine, num: number): string[] {
  const measurement = calcNodeDetailMeasurement(nl);
  const costU        = Number(nl.resolvedUnitCost || 0);
  const costTotal     = measurement * costU;
  const marge         = Number(nl.marge || 0);
  const descompte     = Number(nl.descompte || 0);
  const preu          = costU * marge * (1 - descompte / 100);
  const importNl       = measurement * preu;
  const badge          = badgeFor(nl.childType, nl.nature);
  return [
    String(num), nl.chapter || '', nl.referencia || '', nl.detail || '', nl.observations || '', nl.description || '', '', badge.text,
    nl.unit || '', nl.quantity ? fmt2(nl.quantity) : '',
    nl.dimensionA ? fmt3(nl.dimensionA) : '', nl.dimensionB ? fmt3(nl.dimensionB) : '', nl.dimensionH ? fmt3(nl.dimensionH) : '',
    fmt2(measurement), nl.isPriceLocked ? 'Sí' : '',
    fmt2(costU), fmt2(costTotal), marge ? fmt2(marge) : '', descompte ? fmt2(descompte) : '',
    fmt2(preu), fmt2(importNl), fmt2(nl.pesTotal || 0),
  ];
}

// Línia de pressupost sense desglose vinculat (text lliure) — s'exporta com
// una única fila amb les seves pròpies dades, encaixades a les mateixes
// columnes del desglose perquè no es perdi informació. Sense Cap. propi, cau
// dins del grup "Sense capítol".
function fallbackLineCells(l: BudgetLine, num: number): string[] {
  return [
    String(num), '', '', '', '', l.concepte || '', '', l.tipus || '', l.unitat || '',
    l.medicio ? fmt2(l.medicio) : '', l.llarg ? fmt3(l.llarg) : '', l.ample ? fmt3(l.ample) : '', l.alt ? fmt3(l.alt) : '',
    fmt2(l.amidament), '', '', '', '', l.descompte ? fmt2(l.descompte) : '',
    fmt2(l.preuUA), fmt2(l.import), fmt2(l.pes),
  ];
}

// Subtotals del grup — mateixos que el peu "Totals"/"Subtotal" del Detall
// d'un Escandall (Cost total sumat, Marge mitjà de les línies informades,
// Import sumat, Pes sumat).
interface GroupSubtotal { costTotal: number; margeAvg: number | null; import: number; pes: number }

interface FlatDetailRow {
  chapter: string;
  cells: string[];
  costTotal: number;
  marge: number | null;
  importVal: number;
  pes: number;
  elementImageUrl?: string | null;
}

// Aplana el desglose de totes les línies de pressupost en un únic llistat
// (les línies sense desglose vinculat hi entren com una única fila pròpia).
function flattenDetailRows(lines: BudgetLine[]): FlatDetailRow[] {
  const rows: FlatDetailRow[] = [];
  let num = 0;
  lines.forEach((l) => {
    const detailLines = l.escandallLines ?? [];
    if (detailLines.length > 0) {
      detailLines.forEach((nl) => {
        num += 1;
        const measurement = calcNodeDetailMeasurement(nl);
        const costU       = Number(nl.resolvedUnitCost || 0);
        const costTotal    = measurement * costU;
        const marge        = Number(nl.marge || 0);
        const descompte    = Number(nl.descompte || 0);
        const preu         = costU * marge * (1 - descompte / 100);
        rows.push({
          chapter: nl.chapter?.trim() || SENSE_CAPITOL,
          cells: nodeDetailLineToCells(nl, num),
          costTotal,
          marge: marge > 0 ? marge : null,
          importVal: measurement * preu,
          pes: Number(nl.pesTotal || 0),
          elementImageUrl: nl.elementImageUrl ?? null,
        });
      });
    } else {
      num += 1;
      rows.push({
        chapter: SENSE_CAPITOL,
        cells: fallbackLineCells(l, num),
        costTotal: 0,
        marge: null,
        importVal: Number(l.import || 0),
        pes: Number(l.pes || 0),
      });
    }
  });
  return rows;
}

// Agrupa el llistat aplanat per Capítol (Cap.), amb "Sense capítol" sempre
// al final — mateix ordre que a Escandalls (groupByChapter/NodeForm.tsx).
function groupDetailRowsByChapter(rows: FlatDetailRow[]): { key: string; label: string; rows: { cells: string[]; elementImageUrl?: string | null }[]; subtotal: GroupSubtotal }[] {
  const order: string[] = [];
  const map = new Map<string, FlatDetailRow[]>();
  rows.forEach((row) => {
    if (!map.has(row.chapter)) { order.push(row.chapter); map.set(row.chapter, []); }
    map.get(row.chapter)!.push(row);
  });
  return order
    .sort((a, b) => (a === SENSE_CAPITOL ? 1 : b === SENSE_CAPITOL ? -1 : 0))
    .map((chapter) => {
      const items = map.get(chapter)!;
      const margeInformed = items.map((r) => r.marge).filter((m): m is number => m != null && m > 0);
      return {
        key: chapter,
        label: chapter,
        rows: items.map((r) => ({ cells: r.cells, elementImageUrl: r.elementImageUrl })),
        subtotal: {
          costTotal: items.reduce((s, r) => s + r.costTotal, 0),
          margeAvg:  margeInformed.length > 0 ? margeInformed.reduce((s, m) => s + m, 0) / margeInformed.length : null,
          import:    items.reduce((s, r) => s + r.importVal, 0),
          pes:       items.reduce((s, r) => s + r.pes, 0),
        },
      };
    });
}

function budgetLineGroups(lines: BudgetLine[]) {
  return groupDetailRowsByChapter(flattenDetailRows(lines));
}

function groupSubtotalCells(s: GroupSubtotal): string[] {
  return [fmt2(s.costTotal), s.margeAvg != null ? fmt2(s.margeAvg) : '', '', '', fmt2(s.import), fmt2(s.pes)];
}

// Nombre de decimals que Excel ha de forçar per columna (null = no és
// numèrica) — mateix ordre que DESGLOSE_HEADERS/nodeDetailLineToCells. Sense
// això, Excel interpreta els valors com a números i els mostra amb format
// "General" (retallant els zeros finals: "6,00" -> "6"), donant un nombre de
// decimals diferent a cada cel·la.
const DESGLOSE_NUM_FORMATS: (number | null)[] = [null, null, null, null, null, null, null, null, null, 2, 3, 3, 3, 2, null, 2, 2, 2, 2, 2, 2, 2];

function sanitizeFilename(v: string): string {
  return v.replace(/[/\\:*?"<>|]/g, '-').trim();
}

// Descarrega una imatge i la retorna com a data URI (base64) + extensió, per
// incrustar-la com a imatge nativa dins l'.xlsx (exceljs) — mateix mecanisme
// que a l'export d'Escandalls (NodeForm.tsx).
async function fetchImageForExcel(url: string): Promise<{ dataUri: string; extension: 'jpeg' | 'png' | 'gif' } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const mime = blob.type || '';
    const extension: 'jpeg' | 'png' | 'gif' = mime.includes('png') ? 'png' : mime.includes('gif') ? 'gif' : 'jpeg';
    const dataUri = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { dataUri, extension };
  } catch {
    return null;
  }
}

// Converteix un valor ja formatat amb fmt2/fmt3 (locale ca-ES: punt de milers,
// coma decimal) de nou a número — cal per guardar-lo com a cel·la numèrica
// real a l'.xlsx, arrodonit exactament als decimals mostrats.
function parseLocaleNumber(s: string): number | null {
  if (!s) return null;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

function excelNumFmt(decimals: number): string {
  return decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0';
}

const DESGLOSE_COL_WIDTHS = [4, 8, 12, 24, 22, 28, 6, 7, 6, 6, 7, 7, 7, 10, 5, 9, 10, 7, 9, 8, 9, 7];

// Genera un .xlsx real (via exceljs) amb la miniatura de l'element incrustada
// com a imatge nativa a la cel·la "Foto" — mateix mecanisme que l'export del
// Detall d'un Escandall (NodeForm.tsx): un .xls basat en una taula HTML no
// permet incrustar imatges de manera fiable a Excel.
async function exportBudgetDetallToExcel(filename: string, lines: BudgetLine[]) {
  const groups = budgetLineGroups(lines);
  const costTotalCol = DESGLOSE_HEADERS.indexOf('Cost total');
  const margeCol = DESGLOSE_HEADERS.indexOf('Marge');
  const importCol = DESGLOSE_HEADERS.indexOf('Import');
  const pesCol = DESGLOSE_HEADERS.length - 1;

  const imageUrls = Array.from(new Set(
    groups.flatMap((g) => g.rows.map((r) => r.elementImageUrl).filter((u): u is string => !!u))
  ));
  const imageMap = new Map<string, { dataUri: string; extension: 'jpeg' | 'png' | 'gif' }>();
  await Promise.all(imageUrls.map(async (u) => {
    const img = await fetchImageForExcel(u);
    if (img) imageMap.set(u, img);
  }));

  // Càrrega diferida — exceljs pesa ~1MB minificat, no cal incloure'l al
  // bundle principal si l'usuari no exporta mai a Excel.
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Detall');
  sheet.columns = DESGLOSE_COL_WIDTHS.map((w) => ({ width: w }));

  const headerRow = sheet.addRow(DESGLOSE_HEADERS);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } }; });

  const addSummaryRow = (label: string, s: GroupSubtotal) => {
    const values: (string | number)[] = new Array(DESGLOSE_HEADERS.length).fill('');
    values[costTotalCol] = s.costTotal;
    if (s.margeAvg != null) values[margeCol] = s.margeAvg;
    values[importCol] = s.import;
    values[pesCol] = s.pes;

    const row = sheet.addRow(values);
    row.font = { bold: true };
    row.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }; });
    sheet.mergeCells(row.number, 1, row.number, costTotalCol);
    row.getCell(1).value = label;
    row.getCell(1).alignment = { horizontal: 'right' };
    row.getCell(costTotalCol + 1).numFmt = excelNumFmt(2);
    if (s.margeAvg != null) row.getCell(margeCol + 1).numFmt = excelNumFmt(2);
    row.getCell(importCol + 1).numFmt = excelNumFmt(2);
    row.getCell(pesCol + 1).numFmt = excelNumFmt(2);
  };

  groups.forEach((g) => {
    addSummaryRow(g.label, g.subtotal);

    g.rows.forEach(({ cells, elementImageUrl }) => {
      const values = cells.map((c, i) => {
        if (i === DESGLOSE_FOTO_COL_INDEX) return '';
        const fmt = DESGLOSE_NUM_FORMATS[i];
        return fmt == null ? c : (parseLocaleNumber(c) ?? '');
      });
      const row = sheet.addRow(values);
      cells.forEach((_, i) => {
        const fmt = DESGLOSE_NUM_FORMATS[i];
        if (fmt != null && i !== DESGLOSE_FOTO_COL_INDEX) row.getCell(i + 1).numFmt = excelNumFmt(fmt);
      });
      const img = elementImageUrl ? imageMap.get(elementImageUrl) : null;
      if (img) {
        const imgId = workbook.addImage({ base64: img.dataUri, extension: img.extension });
        sheet.addImage(imgId, {
          tl: { col: DESGLOSE_FOTO_COL_INDEX, row: row.number - 1 },
          ext: { width: 32, height: 32 },
          editAs: 'oneCell',
        } as any);
        row.height = 26;
      }
    });
  });

  if (groups.length > 1) {
    const allSubtotal: GroupSubtotal = {
      costTotal: groups.reduce((s, g) => s + g.subtotal.costTotal, 0),
      margeAvg:  null,
      import:    groups.reduce((s, g) => s + g.subtotal.import, 0),
      pes:       groups.reduce((s, g) => s + g.subtotal.pes, 0),
    };
    addSummaryRow('Totals', allSubtotal);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/\.xls$/i, '.xlsx');
  a.click();
  URL.revokeObjectURL(url);
}

function printBudgetDetallTable(title: string, lines: BudgetLine[]) {
  const groups = budgetLineGroups(lines);
  const win = window.open('', '_blank');
  if (!win) return;
  const bodyHtml = groups.map((g) => {
    const groupRow = `<tr class="grp">`
      + `<td colspan="16" style="text-align:left;">${g.label}</td>`
      + groupSubtotalCells(g.subtotal).map((c) => `<td style="text-align:right;">${c}</td>`).join('')
      + `</tr>`;
    const itemRows = g.rows.map(({ cells, elementImageUrl }) =>
      `<tr>${cells.map((c, i) => (
        i === DESGLOSE_FOTO_COL_INDEX
          ? `<td>${elementImageUrl ? `<img src="${elementImageUrl}" width="28" height="28" style="object-fit:cover;" />` : ''}</td>`
          : `<td>${c}</td>`
      )).join('')}</tr>`,
    ).join('');
    return groupRow + itemRows;
  }).join('');
  win.document.write(`<!doctype html><html><head><title>${title}</title>
    <meta charset="utf-8">
    <style>
      body { font-family: Arial, sans-serif; font-size: 10px; margin: 16px; }
      h1 { font-size: 14px; margin: 0 0 10px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 2px 5px; text-align: right; white-space: nowrap; }
      th:nth-child(-n+6), td:nth-child(-n+6) { text-align: left; white-space: normal; }
      thead { background: #e2e8f0; }
      .grp { text-align: left; font-weight: 600; background: #f1f5f9; }
    </style>
    </head><body>
    <h1>${title}</h1>
    <table>
      <thead><tr>${DESGLOSE_HEADERS.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${bodyHtml}</tbody>
    </table>
    </body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

// ─── Càlculs ─────────────────────────────────────────────────────────────────

function calcLine(l: BudgetLine): BudgetLine {
  const medicio   = l.medicio || 0;
  // Amidament = A × B × C (nomes lectura); ja no s'importa de l'escandall
  // vinculat — si no hi ha dimensions informades, sempre val 1.
  const amidament = (l.llarg || 0) * (l.ample || 0) * (l.alt || 0) || 1;
  // Preu UT = Unitats × Amidament × Preu/UA (Preu/UA es el "Valors/Ut" de
  // Base Imposable de l'escandall vinculat, o un preu manual).
  const preuUT  = round(medicio * amidament * (l.preuUA || 0));
  const imp     = preuUT * (1 - (l.descompte || 0) / 100);
  const horesP  = l.horesP || 0;
  const horesF  = l.horesF || 0;
  const horesM  = l.horesM || 0;
  const horesO  = l.horesO || 0;
  const horesE  = l.horesE || 0;
  // Cost Mat./C.Serveis/C.Indirecte/H.P/H.F/H.M/H.O/H.E son valors unitaris
  // (Valors/Ut, com Cost/UA i Preu/UA); T.Hores es tambe un valor unitari
  // (suma de les hores/Ut), sense multiplicar per Unitats ni Amidament.
  return { ...l, medicio, amidament, preuUT, import: round(imp), totalHores: round(horesP + horesF + horesM + horesO + horesE) };
}

function calcTotals(lines: BudgetLine[], descomptePercent: number, ivaPercent: number) {
  const subtotal          = round(lines.reduce((s, l) => s + (l.import || 0), 0));
  const descompteImport   = round(subtotal * (descomptePercent / 100));
  const baseImposable     = round(subtotal - descompteImport);
  const ivaImport         = round(baseImposable * (ivaPercent / 100));
  const total             = round(baseImposable + ivaImport);
  // Cost Mat., C.Serveis, C.Indirecte, H.P/H.F/H.M/H.O/H.E i T.Hores son
  // valors unitaris ("Valors/Ut", com Cost/UA i Preu/UA) a cada línia — per
  // sumar-los en un total real i comparable amb la Base Imposable (que ja
  // multiplica Unitats × Amidament × Preu/UA), cal desfer aquesta divisió:
  // multiplicar cada línia per Unitats × Amidament, igual que fa l'Import.
  const lineQty = (l: BudgetLine) => (l.medicio || 0) * (l.amidament || 1);
  const totalCostMaterials = round(lines.reduce((s, l) => s + (l.costMaterials || 0) * lineQty(l), 0));
  const totalCostServeis   = round(lines.reduce((s, l) => s + (l.costServeis || 0) * lineQty(l), 0));
  const totalCostIndirecte = round(lines.reduce((s, l) => s + (l.costIndirecte || 0) * lineQty(l), 0));
  const totalHoresP       = round(lines.reduce((s, l) => s + (l.horesP || 0) * lineQty(l), 0));
  const totalHoresF       = round(lines.reduce((s, l) => s + (l.horesF || 0) * lineQty(l), 0));
  const totalHoresM       = round(lines.reduce((s, l) => s + (l.horesM || 0) * lineQty(l), 0));
  const totalHoresO       = round(lines.reduce((s, l) => s + (l.horesO || 0) * lineQty(l), 0));
  const totalHoresE       = round(lines.reduce((s, l) => s + (l.horesE || 0) * lineQty(l), 0));
  const totalHores        = round(lines.reduce((s, l) => s + (l.totalHores || 0) * lineQty(l), 0));
  const totalPes          = round(lines.reduce((s, l) => s + (l.pes || 0) * (l.medicio || 0), 0), 3);
  return { subtotal, descompteImport, baseImposable, ivaImport, total, totalCostMaterials, totalCostServeis, totalCostIndirecte, totalHoresP, totalHoresF, totalHoresM, totalHoresO, totalHoresE, totalHores, totalPes };
}

// Mateixos colors que les etiquetes d'estat de Licitacions (Licitacions.tsx: STATUS_CONFIG)
// pels 3 estats comuns: Licitat=blau (primary), Desestimat=neutre (outline), Proposat=verd (success).
const BUDGET_STATUS_CONFIG: Record<string, { label: string; className: string; dot: string }> = {
  ESBORRANY:  { label: 'Obert',      className: 'bg-gray-100 text-gray-700',    dot: 'bg-gray-400' },
  ENVIAT:     { label: 'Licitat',    className: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-400' },
  DESESTIMAT: { label: 'Desestimat', className: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  ADJUDICAT:  { label: 'Proposat',   className: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
  FACTIBLE:   { label: 'Factible',   className: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  ACCEPTAT:   { label: 'Comanda',    className: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  REBUTJAT:   { label: 'Descartat',  className: 'bg-gray-100 text-gray-600',   dot: 'bg-gray-400' },
  LLIURAT:    { label: 'Lliurat',    className: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
  FACTURAT:   { label: 'Facturat',   className: 'bg-teal-100 text-teal-700',    dot: 'bg-teal-400' },
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function BudgetForm() {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const ecIdParam = searchParams.get('ecId') ?? '';
  const isNew     = !id || id === 'new';

  const [tab, setTab]           = useState<'general' | 'detall'>('general');
  // Targetes col·lapsables de la columna dreta (Dades Generals), mateix
  // patró que ExpedientComercialForm.tsx.
  const [entregaOpen, setEntregaOpen] = useState(false);
  const [pagamentOpen, setPagamentOpen] = useState(false);
  const [ratisOpen, setRatisOpen] = useState(false);
  const [dadesEconomiquesOpen, setDadesEconomiquesOpen] = useState(false);
  const [graficOpen, setGraficOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [documents, setDocuments] = useState<BudgetDocumentRow[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingDocDescription, setEditingDocDescription] = useState('');
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [form, setForm]         = useState<BudgetForm>(EMPTY_FORM());
  const [budgetNumber, setBudgetNumber] = useState<string>('(auto)');
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [loading, setLoading]   = useState(!isNew);
  const [saving, setSaving]     = useState(false);
  const [clients, setClients]       = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientOpen, setClientOpen]     = useState(false);
  const [contacts, setContacts]     = useState<Contact[]>([]);
  const [clientAddresses, setClientAddresses] = useState<ClientAddressOption[]>([]);
  // GPS de l'adreça triada al desplegable de "Lloc de lliurament" — es
  // manté a part (no barrejat dins del text lliure) perque el link de
  // Google Maps funcioni; s'esborra si l'usuari edita el text a ma.
  const [llocLliuramentGps, setLlocLliuramentGps] = useState('');
  const [nodes, setNodes]           = useState<Node[]>([]);
  const [brands, setBrands]         = useState<Brand[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [estatsOpts, setEstatsOpts] = useState<{ id: number; code: string; name: string }[]>([]);
  const [versionsOpts, setVersionsOpts] = useState<{ id: number; code: string; name: string }[]>([]);
  const [idiomesOpts, setIdiomesOpts] = useState<{ id: number; code: string; name: string }[]>([]);

  // Edicio de la taula Detall — inputs sempre visibles (com Escandalls), sense
  // toggle de click-per-editar. editCells guarda el text en curs mentre s'edita
  // una cel·la numerica, per no perdre la coma decimal fins que es fa blur.
  const [editCells, setEditCells]   = useState<Record<string, string>>({});

  // Files amb el detall complet de l'escandall vinculat (totes les seves línies
  // de composició, editables com a còpia local) desplegades — nomes te sentit
  // per a línies amb nodeId. Colapsat per defecte; les línies es carreguen sota
  // demanda i es guarden a `escandallLines` de la propia línia de pressupost
  // (es persisteixen amb el pressupost, sense tocar mai l'escandall original).
  const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set());
  const [nodeDetailLoading, setNodeDetailLoading] = useState<Set<number>>(new Set());
  const [compositeCodes, setCompositeCodes] = useState<CompositeCodeOption[]>([]);

  useEffect(() => {
    api.get<{ success: boolean; data: CompositeCodeOption[] }>('/nodes/composite-codes')
      .then((r) => setCompositeCodes(r.data ?? []))
      .catch(() => setCompositeCodes([]));
  }, []);

  // Marge per defecte d'una línia importada de l'escandall (quan no en te de
  // fixat): el "Marge comercial %" del Tipus de Producte vinculat — mateixa
  // lògica que NodeForm.tsx (marginForLine). Nomes s'usa una vegada, en crear
  // la còpia local; a partir d'aquí el Marge es un camp editable normal.
  const marginForRawNodeLine = (compositeCodeId: number | null | undefined): number => {
    if (!compositeCodeId) return 0;
    const cc = compositeCodes.find((c) => c.id === compositeCodeId);
    return cc?.marginPercentage != null ? Number(cc.marginPercentage) : 0;
  };

  const rawLineToDetailLine = (raw: any): NodeDetailLine => {
    const marge = raw.marge != null ? Number(raw.marge) : marginForRawNodeLine(raw.compositeCodeId);
    const base = {
      childType: raw.childType,
      description: raw.description ?? '',
      nature: raw.nature ?? 'MATERIAL',
      unit: raw.unit ?? 'Ut',
      chapter: raw.chapter ?? '',
      referencia: raw.referencia ?? '',
      detail: raw.detail ?? '',
      quantity: Number(raw.quantity ?? 0),
      dimensionA: Number(raw.dimensionA ?? 0),
      dimensionB: Number(raw.dimensionB ?? 0),
      dimensionH: Number(raw.dimensionH ?? 0),
      isPriceLocked: !!raw.isPriceLocked,
      resolvedUnitCost: Number(raw.resolvedUnitCost ?? 0),
      marge,
      descompte: Number(raw.descompte ?? 0),
      pesTotal: 0,
      weightKgPerUnit: Number(raw.weightKg ?? 0),
      observations: raw.observations ?? '',
      elementImageUrl: raw.elementImageUrl ?? null,
      nodeHoresP: raw.childType === 'NODE'
        ? Number(raw.childHoursOwn ?? 0) + Number(raw.childHoursSubcontract ?? 0)
        : 0,
    };
    return { ...base, pesTotal: Number(raw.weightKg ?? 0) * calcNodeDetailMeasurement(base) };
  };

  // Aplica un nou desglose (escandallLines) a la línia ri i recalcula Preu/U,
  // Cost/UA i Pes de la capçalera a partir d'aquest desglose (Import/Cost
  // total sumat i dividit per l'Amidament propi de l'escandall), per aixì
  // reflectir sempre qualsevol edició/afegit/eliminat de línia del detall.
  const applyDetailLines = (ri: number, detailLines: NodeDetailLine[]) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[ri] = calcLine(mergeEscandallDerived({ ...lines[ri], escandallLines: detailLines }));
      return { ...f, lines };
    });
  };

  // Camps que afecten la Medició (quantity * A * B * H) — en canviar-ne un,
  // es recalcula pesTotal a partir del pes unitari del material/escandall
  // vinculat (weightKgPerUnit), igual que ja passa amb el Cost total. Nomes
  // afecta línies vinculades a un material/escandall real (weightKgPerUnit>0);
  // les línies de text lliure sense pes catalogat no es toquen.
  const DETAIL_MEASUREMENT_FIELDS: (keyof NodeDetailLine)[] = ['quantity', 'dimensionA', 'dimensionB', 'dimensionH'];

  const updateDetailLine = (ri: number, ni: number, field: keyof NodeDetailLine, value: unknown) => {
    const detailLines = [...(form.lines[ri].escandallLines ?? [])];
    const nl = { ...detailLines[ni], [field]: value };
    if (DETAIL_MEASUREMENT_FIELDS.includes(field) && Number(nl.weightKgPerUnit)) {
      nl.pesTotal = Number(nl.weightKgPerUnit) * calcNodeDetailMeasurement(nl);
    }
    detailLines[ni] = nl;
    applyDetailLines(ri, detailLines);
  };

  const patchDetailLine = (ri: number, ni: number, patch: Partial<NodeDetailLine>) => {
    const detailLines = [...(form.lines[ri].escandallLines ?? [])];
    detailLines[ni] = { ...detailLines[ni], ...patch };
    applyDetailLines(ri, detailLines);
  };

  // ── Autocomplete d'"Element" al desglose local ──────────────────────────
  const clearDetailAutocomplete = () => {
    setMaterialSuggestions([]);
    setNodeSuggestions([]);
    setActiveDetailSearch(null);
    setDetailDropdownRect(null);
  };

  const searchDetailItems = async (query: string, ri: number, ni: number) => {
    if (query.length < 2) { clearDetailAutocomplete(); return; }
    setActiveDetailSearch({ ri, ni });
    setSearchingDetail(true);
    try {
      const matRes = await api.get<{ success: boolean; data: SimpleMaterial[] }>(
        `/simple-materials/search?q=${encodeURIComponent(query)}&limit=10`,
      );
      setMaterialSuggestions(matRes.data ?? []);
      const q = query.toLowerCase();
      setNodeSuggestions(nodes.filter((n) =>
        n.name.toLowerCase().includes(q) || (n.code ?? '').toLowerCase().includes(q),
      ).slice(0, 10));
    } catch {
      setMaterialSuggestions([]);
      setNodeSuggestions([]);
    } finally {
      setSearchingDetail(false);
    }
  };

  const selectDetailMaterial = (mat: SimpleMaterial, ri: number, ni: number) => {
    const et = mat.elementTypeId != null ? elementTypes.find((e) => e.id === mat.elementTypeId) : undefined;
    const nature = natureForElementType(et) ?? 'MATERIAL';
    const isLabor = nature === 'MO_PROPIA' || nature === 'MO_SUBCONTRACT';
    const current = form.lines[ri].escandallLines?.[ni];
    const measurement = current ? calcNodeDetailMeasurement(current) : 0;
    patchDetailLine(ri, ni, {
      childType: 'MATERIAL',
      description: mat.name,
      unit: isLabor ? 'H' : mat.unit,
      nature,
      resolvedUnitCost: Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0),
      elementImageUrl: mat.technicalImageUrl ?? null,
      weightKgPerUnit: Number(mat.weightKgPerUnit ?? 0),
      pesTotal: Number(mat.weightKgPerUnit ?? 0) * measurement,
    });
    clearDetailAutocomplete();
  };

  const selectDetailNode = (node: Node, ri: number, ni: number) => {
    const current = form.lines[ri].escandallLines?.[ni];
    const measurement = current ? calcNodeDetailMeasurement(current) : 0;
    patchDetailLine(ri, ni, {
      childType: 'NODE',
      description: node.name,
      resolvedUnitCost: Number(node.cachedTotalCost ?? 0),
      nodeHoresP: Number(node.cachedTotalHoursOwn ?? 0) + Number(node.cachedTotalHoursSubcontract ?? 0),
      weightKgPerUnit: Number(node.cachedTotalWeightKg ?? 0),
      pesTotal: Number(node.cachedTotalWeightKg ?? 0) * measurement,
    });
    clearDetailAutocomplete();
  };

  const addDetailLine = (ri: number) => {
    const detailLines = [...(form.lines[ri].escandallLines ?? [])];
    detailLines.push({
      childType: 'MATERIAL', description: '', nature: 'MATERIAL', unit: 'Ut',
      chapter: '', referencia: '', detail: '', quantity: 0,
      dimensionA: 0, dimensionB: 0, dimensionH: 0, isPriceLocked: false,
      resolvedUnitCost: 0, marge: 0, descompte: 0, pesTotal: 0, weightKgPerUnit: 0,
      observations: '', elementImageUrl: null,
    });
    applyDetailLines(ri, detailLines);
  };

  const removeDetailLine = (ri: number, ni: number) => {
    const detailLines = (form.lines[ri].escandallLines ?? []).filter((_, i) => i !== ni);
    applyDetailLines(ri, detailLines);
  };

  // Reordenació per arrossegament de les línies del desglose, igual que a
  // Escandalls (NodeForm.tsx) — dragFrom es la línia arrossegada (identificada
  // per ri+ni, ja que cada línia de pressupost te el seu propi desglose local).
  const [dragDetailFrom, setDragDetailFrom] = useState<{ ri: number; ni: number } | null>(null);
  const [dropDetailTarget, setDropDetailTarget] = useState<number | null>(null);

  // Buffer local del camp "Cap." — les línies es reagrupen per capítol en
  // renderitzar-se (igual que a Escandalls), així que escriure directament a
  // nl.chapter en cada tecla reagrupa la línia (i re-munta l'input) després de
  // cada caràcter. S'escriu aquí mentre s'edita i es desa a onBlur, igual que
  // el patró "chapterDrafts" de NodeForm.tsx.
  const [capitolDrafts, setCapitolDrafts] = useState<Record<string, string>>({});

  const handleDetailDragStart = (ri: number, ni: number) => setDragDetailFrom({ ri, ni });
  const handleDetailDragEnd = () => { setDragDetailFrom(null); setDropDetailTarget(null); };
  const handleDetailDrop = (ri: number, ni: number) => {
    if (dragDetailFrom && dragDetailFrom.ri === ri && dragDetailFrom.ni !== ni) {
      const detailLines = [...(form.lines[ri].escandallLines ?? [])];
      const [moved] = detailLines.splice(dragDetailFrom.ni, 1);
      detailLines.splice(ni, 0, moved);
      applyDetailLines(ri, detailLines);
    }
    setDragDetailFrom(null);
    setDropDetailTarget(null);
  };

  const toggleExpandLine = (ri: number, nodeId?: string) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(ri)) next.delete(ri); else next.add(ri);
      return next;
    });
    // Es carrega el desglose (i s'omple escandallAmidament si encara falta,
    // p.ex. línies vinculades abans d'aquest camp) nomes la primera vegada.
    if (nodeId && (!form.lines[ri]?.escandallLines || form.lines[ri]?.escandallAmidament == null)) {
      setNodeDetailLoading((p) => new Set(p).add(ri));
      api.get<{ success: boolean; data: { lines?: any[]; amidament?: number } }>(`/nodes/${nodeId}`)
        .then((res) => {
          const amid = Number(res.data?.amidament ?? 1) || 1;
          setForm((f) => {
            const lines = [...f.lines];
            const detailLines = lines[ri]?.escandallLines ?? (res.data?.lines ?? []).map(rawLineToDetailLine);
            lines[ri] = { ...lines[ri], escandallLines: detailLines, escandallAmidament: amid };
            return { ...f, lines };
          });
        })
        .finally(() => setNodeDetailLoading((p) => { const n = new Set(p); n.delete(ri); return n; }));
    }
  };

  const [ecInfo, setEcInfo] = useState<{ id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; clientId: string; brandId?: string | null } | null>(null);

  // Selector d'Expedient Comercial quan es crea un pressupost directament des
  // de Pressupostos > Nou Pressupost (sense ecId a la URL) — permet buscar i
  // assignar-lo manualment, igual que des de la fitxa de l'EC.
  const [ecSearch, setEcSearch] = useState('');
  const [ecOpen, setEcOpen] = useState(false);
  const [ecResults, setEcResults] = useState<{ id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; clientId: string; brandId?: string | null; client?: { name: string } }[]>([]);
  const [searchingEc, setSearchingEc] = useState(false);

  const searchEcs = async (query: string) => {
    setEcSearch(query);
    if (query.trim().length < 2) { setEcResults([]); return; }
    setSearchingEc(true);
    try {
      const res = await api.get<{ success: boolean; data: typeof ecResults }>('/expedients-comercials', { search: query, limit: 20 });
      setEcResults(res.data ?? []);
    } catch { setEcResults([]); }
    finally { setSearchingEc(false); }
  };

  const selectEc = (ec: { id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; clientId: string; brandId?: string | null }) => {
    setEcInfo({ id: ec.id, ecNumber: ec.ecNumber, name: ec.name, etiquetaExpedient: ec.etiquetaExpedient ?? null, clientId: ec.clientId, brandId: ec.brandId ?? null });
    setForm((f) => ({ ...f, ecId: ec.id, clientId: ec.clientId, brandId: ec.brandId ?? null, projecte: (ec.etiquetaExpedient || ec.name) ?? f.projecte, contactId: '' }));
    setEcSearch('');
    setEcOpen(false);
    loadContacts(ec.clientId);
    loadClientAddresses(ec.clientId);
    applyClientIdioma(clients.find((c) => c.id === ec.clientId));
  };

  const [companyUsers, setCompanyUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [metodesPagament, setMetodesPagament] = useState<{ id: number; name: string }[]>([]);

  // Email modal
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
  const [emailModal, setEmailModal] = useState(false);
  const [emailTo, setEmailTo]       = useState('');
  const [emailBody, setEmailBody]   = useState('');

  // Modal de duplicar — permet mantenir l'Expedient Comercial actual o
  // assignar-ne un altre del mateix client.
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateEcOption, setDuplicateEcOption] = useState<'same' | 'other'>('same');
  const [duplicateEcId, setDuplicateEcId] = useState('');
  const [duplicateEcSelected, setDuplicateEcSelected] = useState<{ id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; client?: { name: string } } | null>(null);
  const [duplicateEcSearch, setDuplicateEcSearch] = useState('');
  const [duplicateEcResults, setDuplicateEcResults] = useState<{ id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; client?: { name: string } }[]>([]);
  const [searchingDuplicateEc, setSearchingDuplicateEc] = useState(false);

  // Node search popup
  const [nodeSearch, setNodeSearch]   = useState('');
  const [nodePopupRow, setNodePopupRow] = useState<number | null>(null);

  // Autocomplete d'"Element" a les línies del desglose local (igual que a
  // Escandalls/NodeForm.tsx) — cerca materials en viu i escandalls sobre la
  // llista ja carregada de `nodes`.
  const [elementTypes, setElementTypes] = useState<ElementTypeOption[]>([]);
  const [materialSuggestions, setMaterialSuggestions] = useState<SimpleMaterial[]>([]);
  const [nodeSuggestions, setNodeSuggestions] = useState<Node[]>([]);
  const [activeDetailSearch, setActiveDetailSearch] = useState<{ ri: number; ni: number } | null>(null);
  const [detailDropdownRect, setDetailDropdownRect] = useState<DOMRect | null>(null);
  const [searchingDetail, setSearchingDetail] = useState(false);

  // ── Catàlegs de pressupost ───────────────────────────────────────────────

  useEffect(() => {
    const empty: { id: number; code: string; name: string; isActive: number }[] = [];
    Promise.all([
      api.get<{ success: boolean; data: typeof empty }>('/admin/estats-crm', { module: 'pressupost' }).catch(() => ({ data: empty })),
      api.get<{ success: boolean; data: typeof empty }>('/admin/versions-pressupost').catch(() => ({ data: empty })),
      api.get<{ success: boolean; data: typeof empty }>('/admin/idiomes-pressupost').catch(() => ({ data: empty })),
    ]).then(([e, v, i]) => {
      setEstatsOpts((e.data ?? []).filter(x => x.isActive));
      setVersionsOpts((v.data ?? []).filter(x => x.isActive));
      setIdiomesOpts((i.data ?? []).filter(x => x.isActive));
    });
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: { id: string; firstName: string; lastName: string }[] }>('/auth/users')
      .then((r) => setCompanyUsers(r.data ?? []))
      .catch(() => setCompanyUsers([]));
    api.get<{ success: boolean; data: { id: number; name: string; isActive: number }[] }>('/admin/metodes-pagament')
      .then((r) => setMetodesPagament((r.data ?? []).filter((m) => m.isActive)))
      .catch(() => setMetodesPagament([]));
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: UnitType[] }>('/nodes/unit-types')
      .then((r) => setUnitTypes(r.data ?? []))
      .catch(() => setUnitTypes([]));
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: ElementTypeOption[] }>('/simple-materials/element-types')
      .then((r) => setElementTypes(r.data ?? []))
      .catch(() => setElementTypes([]));
  }, []);

  // Sincronitza sempre les 5 tarifes horaries amb el Cost/UA dels elements
  // de mà d'obra del catàleg, arrodonit a 2 decimals. S'identifiquen per id
  // (estable) i no per codi, ja que el recodificat massiu d'Elements
  // (2026-08-28) reassigna els codis de text segons Tipus+Familia+Producte.
  // S'espera a que hagi acabat la càrrega (loading) perquè no hi hagi
  // carrera amb el fetch del pressupost.
  useEffect(() => {
    if (loading) return;
    const idToField: { id: string; field: 'horaOficina' | 'horaFabricacio' | 'horaTaller' | 'horaMuntatge' | 'horaExtern' }[] = [
      { id: '099fb44a-d0b1-466f-a6db-40a4a3a7a0c1', field: 'horaOficina' },    // 241 - Operari oficina tècnica
      { id: 'b7f6f628-a112-4b6b-8d4b-ac11b17fba04', field: 'horaFabricacio' }, // 239 - Operari de taller per fabricació
      { id: '163b1cb0-4be8-4609-9d07-1e36d65dbfcc', field: 'horaTaller' },    // 237 - Operari a taller per producció
      { id: 'd819aff6-beb9-4ea1-a0e5-017144def28d', field: 'horaMuntatge' },  // 238 - Operari de muntatge amb dietes
      { id: '643ae743-59cc-4c92-b10e-f18d240ecc56', field: 'horaExtern' },    // 240 - Operari extern
    ];
    Promise.all(idToField.map(({ id }) =>
      api.get<{ success: boolean; data: { purchaseCost: number; priceFactor: number; wastePercentage: number } }>(`/simple-materials/${id}`)
        .then((r) => r.data)
        .catch(() => undefined)
    )).then((results) => {
      setForm((f) => {
        const updates: Partial<BudgetForm> = {};
        results.forEach((m, i) => {
          const { field } = idToField[i];
          if (!m) return;
          const purchaseCost = Number(m.purchaseCost ?? 0);
          const priceFactor  = Number(m.priceFactor ?? 1);
          const waste        = Number(m.wastePercentage ?? 0);
          const wasteFactor  = waste < 100 ? 1 / (1 - waste / 100) : 1;
          const pricePerMagnitude = purchaseCost * priceFactor * wasteFactor;
          if (pricePerMagnitude > 0) (updates as any)[field] = Math.round(pricePerMagnitude * 100) / 100;
        });
        return { ...f, ...updates };
      });
    });
  }, [loading]);

  // ── Carrega inicial ──────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const empty = { success: true, data: [] };
        const [clientsRes, nodesRes, brandsRes, bankRes] = await Promise.all([
          api.get<{ success: boolean; data: Client[] }>('/clients', { limit: 500, isActive: 1 }),
          api.get<{ success: boolean; data: Node[] }>('/nodes',   { limit: 500, isActive: 1 }),
          api.get<{ success: boolean; data: Brand[] }>('/brands').catch(() => empty),
          api.get<{ success: boolean; data: BankAccount[] }>('/admin/settings/bank-accounts').catch(() => empty),
        ]);
        setClients(clientsRes.data ?? []);
        setNodes(nodesRes.data ?? []);
        setBrands(brandsRes.data ?? []);
        setBankAccounts(bankRes.data ?? []);

        if (isNew && ecIdParam) {
          try {
            const ecRes = await api.get<{ success: boolean; data: any }>(`/expedients-comercials/${ecIdParam}`);
            const ec = ecRes.data;
            setEcInfo({ id: ec.id, ecNumber: ec.ecNumber, name: ec.name, etiquetaExpedient: ec.etiquetaExpedient ?? null, clientId: ec.clientId, brandId: ec.brandId ?? null });
            setForm((f) => ({ ...f, ecId: ec.id, clientId: ec.clientId, brandId: ec.brandId ?? null, projecte: (ec.etiquetaExpedient || ec.name) ?? f.projecte }));
            if (ec.clientId) {
              const clientContacts = await loadContacts(ec.clientId);
              loadClientAddresses(ec.clientId);
              setClientSearch(ec.client?.name ?? '');
              // Resol l'idioma directament contra el catàleg (no depèn de
              // l'estat idiomesOpts, que es carrega en un efecte a part i
              // podria no estar encara disponible en aquest punt).
              if (ec.client?.idiomaPressupostId) {
                api.get<{ success: boolean; data: { id: number; code: string }[] }>('/admin/idiomes-pressupost')
                  .then((r) => {
                    const idioma = (r.data ?? []).find((o) => o.id === ec.client.idiomaPressupostId);
                    if (idioma) setForm((f) => ({ ...f, idm: idioma.code }));
                  })
                  .catch(() => {});
              }
              // Preselecciona el contacte indicat a l'EC (responsableProjecte, guardat
              // com a text lliure "Nom Cognom"); si no coincideix amb cap contacte del
              // client, es cau al contacte marcat com a principal.
              const ecContactName = (ec.responsableProjecte ?? '').trim().toLowerCase();
              const matched = ecContactName
                ? clientContacts.find((c) => `${c.firstName} ${c.lastName}`.trim().toLowerCase() === ecContactName)
                : undefined;
              const chosen = matched ?? clientContacts.find((c) => c.isPrimary);
              if (chosen) setForm((f) => ({ ...f, contactId: chosen.id }));
            }
          } catch { /* EC no trobada — es deixa sense preseleccionar */ }
        }

        if (!isNew && id) {
          const res = await api.get<{ success: boolean; data: any }>(`/budgets/${id}`);
          const b   = res.data;
          setBudgetNumber(b.budgetNumber);
          setAcceptedAt(b.acceptedAt ?? null);
          if (b.ec) setEcInfo({ id: b.ec.id, ecNumber: b.ec.ecNumber, name: b.ec.name, etiquetaExpedient: b.ec.etiquetaExpedient ?? null, clientId: b.ec.clientId, brandId: b.ec.brandId ?? null });
          setForm({
            ecId:               b.ecId ?? '',
            budgetDate:         b.budgetDate ?? '',
            validityDate:       b.validityDate ?? '',
            estat:              b.estat ?? 'ESBORRANY',
            idm:                b.idm ?? 'CA',
            brandId:            b.brandId ?? null,
            clientId:           b.clientId ?? '',
            contactId:          b.contactId ?? '',
            projecte:           b.projecte ?? '',
            entradeta:          b.entradeta ?? '',
            nostraReferencia:   b.nostraReferencia ?? '',
            versio:             b.versio ?? 'ESCANDALLS',
            amidament:          Number(b.amidament ?? 1),
            unitatAmidament:    b.unitatAmidament ?? 'Ut',
            lliuramentPrevist:  b.lliuramentPrevist ?? '',
            condicioLliurament: b.condicioLliurament ?? '',
            llocLliurament:     b.llocLliurament ?? '',
            ports:              b.ports ?? '',
            sistPagament:       b.sistPagament ?? '',
            venciment:          b.venciment ?? '',
            numCompte:          b.numCompte ?? '',
            notes:              b.notes ?? '',
            observacions:       b.observacions ?? '',
            descomptePercent:   Number(b.descomptePercent ?? 0),
            ivaPercent:         Number(b.ivaPercent ?? 21),
            horaTaller:         Number(b.horaTaller ?? 22.11),
            horaMuntatge:       Number(b.horaMuntatge ?? 26.67),
            horaOficina:        Number(b.horaOficina ?? 22.11),
            horaExtern:         Number(b.horaExtern ?? 22.11),
            horaFabricacio:     Number(b.horaFabricacio ?? 22.11),
            lines:              (b.lines ?? []).map((l: any, i: number) => calcLine(mergeEscandallDerived({
              ...EMPTY_LINE(), ...l,
              ordre: l.ordre ?? i,
              llarg: Number(l.llarg ?? 0), ample: Number(l.ample ?? 0), alt: Number(l.alt ?? 0),
              medicio: Number(l.medicio ?? 0), amidament: Number(l.amidament ?? 1), preuUA: Number(l.preuUA ?? 0), preuUT: Number(l.preuUT ?? 0),
              descompte: Number(l.descompte ?? 0), import: Number(l.import ?? 0),
              costMaterials: Number(l.costMaterials ?? 0),
              costServeis: Number(l.costServeis ?? 0), costIndirecte: Number(l.costIndirecte ?? 0),
              horesP: Number(l.horesP ?? 0),
              horesF: Number(l.horesF ?? 0), horesM: Number(l.horesM ?? 0),
              horesO: Number(l.horesO ?? 0), horesE: Number(l.horesE ?? 0),
              totalHores: Number(l.totalHores ?? 0), totalCost: Number(l.totalCost ?? 0),
              pes: Number(l.pes ?? 0), factor: Number(l.factor ?? 1),
              escandallAmidament: l.escandallAmidament != null ? Number(l.escandallAmidament) : undefined,
            }))),
          });
          if (b.clientId) {
            loadContacts(b.clientId);
            loadClientAddresses(b.clientId);
            setClientSearch(b.client?.name ?? '');
          }
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const loadContacts = async (clientId: string): Promise<Contact[]> => {
    if (!clientId) { setContacts([]); return []; }
    try {
      const res = await api.get<{ success: boolean; data: Contact[] }>(`/clients/${clientId}/contacts`);
      const list = res.data ?? [];
      setContacts(list);
      return list;
    } catch { setContacts([]); return []; }
  };

  // Compon el text complet d'una Adreça del client (tots els camps
  // disponibles) per bolcar-lo al camp de text lliure "Lloc de lliurament".
  const formatClientAddress = (a: ClientAddressOption): string => {
    const parts = [a.address];
    if (a.notes) parts.push(a.notes);
    const body = parts.filter(Boolean).join(' — ');
    return a.label ? `${a.label}: ${body}` : body;
  };

  // No hi ha endpoint dedicat de "adreces" — venen embegudes dins de la
  // fitxa del client (GET /clients/:id).
  const loadClientAddresses = async (clientId: string): Promise<ClientAddressOption[]> => {
    if (!clientId) { setClientAddresses([]); return []; }
    try {
      const res = await api.get<{ success: boolean; data: { addresses?: ClientAddressOption[] } }>(`/clients/${clientId}`);
      const list = res.data?.addresses ?? [];
      setClientAddresses(list);
      return list;
    } catch { setClientAddresses([]); return []; }
  };

  // El PDF del pressupost s'imprimeix en l'idioma del client (camp "Idioma"
  // de la fitxa de client, catàleg idiomes-pressupost) — en seleccionar un
  // client, s'aplica automaticament el seu idioma al camp `idm` del
  // pressupost (que és el que llegeix el generador de PDF).
  const applyClientIdioma = (client?: { idiomaPressupostId?: number } | null) => {
    if (!client?.idiomaPressupostId) return;
    const idioma = idiomesOpts.find((o) => o.id === client.idiomaPressupostId);
    if (idioma) setForm((f) => ({ ...f, idm: idioma.code }));
  };

  // ── Gestió de línies ─────────────────────────────────────────────────────

  const addLine = () => {
    setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE(), ordre: f.lines.length }] }));
  };

  const removeLine = (i: number) => {
    setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, ordre: idx })) }));
  };

  const updateLine = (i: number, field: keyof BudgetLine, value: unknown) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[i] = calcLine({ ...lines[i], [field]: value } as BudgetLine);
      return { ...f, lines };
    });
  };

  // Reordenació per arrossegament de les línies principals del pressupost
  // (mateix mecanisme que el desglose local, veure handleDetailDragStart).
  const [dragLineFrom, setDragLineFrom] = useState<number | null>(null);
  const [dropLineTarget, setDropLineTarget] = useState<number | null>(null);
  const handleLineDragStart = (ri: number) => setDragLineFrom(ri);
  const handleLineDragEnd = () => { setDragLineFrom(null); setDropLineTarget(null); };
  const handleLineDrop = (toRi: number) => {
    if (dragLineFrom !== null && dragLineFrom !== toRi) {
      setForm((f) => {
        const lines = [...f.lines];
        const [moved] = lines.splice(dragLineFrom, 1);
        lines.splice(toRi, 0, moved);
        return { ...f, lines: lines.map((l, idx) => ({ ...l, ordre: idx })) };
      });
      // L'estat d'expandit es basa en l'índex de fila — es neteja perquè no
      // quedi assignat a una línia diferent despres de reordenar.
      setExpandedLines(new Set());
    }
    setDragLineFrom(null);
    setDropLineTarget(null);
  };

  const numCellInput = (ri: number, col: keyof BudgetLine) => {
    const key = `${ri}_${String(col)}`;
    const stored = editCells[key];
    const numVal = Number(form.lines[ri][col] ?? 0);
    return (
      <Input
        type="text" inputMode="decimal"
        value={stored !== undefined ? stored : (numVal === 0 ? '' : fmt2(numVal))}
        onFocus={() => setEditCells((p) => ({ ...p, [key]: numVal === 0 ? '' : String(numVal).replace('.', ',') }))}
        onChange={(e) => setEditCells((p) => ({ ...p, [key]: e.target.value }))}
        onBlur={() => {
          const parsed = parseFloat((editCells[key] ?? '').replace(',', '.')) || 0;
          updateLine(ri, col, parsed);
          setEditCells((p) => { const n = { ...p }; delete n[key]; return n; });
        }}
        placeholder="0"
        className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }}
      />
    );
  };

  // Cel·la numèrica editable per a una línia del snapshot local d'un escandall
  // vinculat (form.lines[ri].escandallLines[ni]) — mateix patró que numCellInput.
  const detailNumCellInput = (ri: number, ni: number, field: keyof NodeDetailLine, decimals: 2 | 3 = 2) => {
    const key = `d_${ri}_${ni}_${String(field)}`;
    const stored = editCells[key];
    const numVal = Number((form.lines[ri].escandallLines?.[ni] as any)?.[field] ?? 0);
    const fmt = decimals === 3 ? fmt3 : fmt2;
    return (
      <Input
        type="text" inputMode="decimal"
        value={stored !== undefined ? stored : (numVal === 0 ? '' : fmt(numVal))}
        onFocus={() => setEditCells((p) => ({ ...p, [key]: numVal === 0 ? '' : String(numVal).replace('.', ',') }))}
        onChange={(e) => setEditCells((p) => ({ ...p, [key]: e.target.value }))}
        onBlur={() => {
          const parsed = parseFloat((editCells[key] ?? '').replace(',', '.')) || 0;
          updateDetailLine(ri, ni, field, parsed);
          setEditCells((p) => { const n = { ...p }; delete n[key]; return n; });
        }}
        placeholder="0"
        className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }}
      />
    );
  };

  const importNode = async (rowIdx: number, node: Node) => {
    // Tot (Cost/UA, Preu/U, Pes, Cost Mat./Serveis, Hores) es deriva del
    // desglose local que es crea en vincular l'escandall, sumant les seves
    // línies de composició SENSE dividir per l'Amidament — Preu/U es el
    // total de l'escandall sencer (amb Medició=1 per defecte), no un preu
    // per unitat de mesura com fa el propi escandall. El Cost Indirecte és
    // l'única excepció (a l'escandall real és un % configurat a nivell de
    // node sobre el Cost Materials, no derivable de les línies) — es manté
    // el valor cachejat del node (total, sense dividir) en el moment
    // d'importar.
    let nodeAmidament = 1;
    let nodeLines: any[] = [];
    let costIndirecteTotal = 0;
    try {
      const res = await api.get<{ success: boolean; data: { lines?: any[]; cachedTotalIndirectCost?: number; amidament?: number } }>(`/nodes/${node.id}`);
      nodeLines = res.data?.lines ?? [];
      nodeAmidament = Number(res.data?.amidament ?? 1) || 1;
      costIndirecteTotal = Number(res.data?.cachedTotalIndirectCost ?? 0);
    } catch { /* es cau als valors cachejats del node com a únic recurs disponible */ }

    const escandallLines = nodeLines.map(rawLineToDetailLine);
    const derived = escandallLines.length > 0
      ? recalcHeaderFromDetail(escandallLines, nodeAmidament)
      : {
          totalCost: Number(node.cachedTotalCost ?? 0), preuUA: Number(node.preuVenda ?? 0),
          pes: Number(node.cachedTotalWeightKg ?? 0),
          costMaterials: Number(node.cachedTotalMaterialsCost ?? 0), costServeis: 0,
          horesO: 0, horesF: 0, horesM: 0, horesE: 0,
          horesP: Number(node.cachedTotalHoursOwn ?? 0) + Number(node.cachedTotalHoursSubcontract ?? 0),
        };

    setForm((f) => {
      const lines = [...f.lines];
      lines[rowIdx] = calcLine({
        ...lines[rowIdx],
        nodeId: node.id,
        nodeName: node.name,
        concepte: lines[rowIdx].concepte || node.name,
        medicio: lines[rowIdx].medicio || 1,
        ...derived,
        costIndirecte: costIndirecteTotal,
        escandallLines,
        escandallAmidament: nodeAmidament,
      });
      return { ...f, lines };
    });
    setNodePopupRow(null);
    setNodeSearch('');
  };

  // ── Desar ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.clientId) { alert('Cal seleccionar un client'); return; }
    setSaving(true);
    try {
      const dateOrNull = (v: string) => v || null;
      const payload = {
        ...form,
        ecId:              form.ecId || null,
        validityDate:      dateOrNull(form.validityDate),
        lines: form.lines.map((l, i) => ({ ...l, ordre: i })),
      };
      if (isNew) {
        await api.post<{ success: boolean; data: any }>('/budgets', payload);
      } else {
        await api.put(`/budgets/${id}`, payload);
      }
      sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('pressupostos'));
      navigate('/crm');
    } catch (err: any) {
      alert('Error desant: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await apiClient.get(`/budgets/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href    = url;
      a.download = `${budgetNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error generant el PDF');
    }
  };

  const handlePrintPdf = async () => {
    try {
      const res = await apiClient.get(`/budgets/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      alert('Error generant el PDF');
    }
  };

  const handleSendEmail = () => {
    if (!emailTo) { alert('Introdueix l\'adreça de destinatari'); return; }
    const subject = `Pressupost ${budgetNumber}`;
    const body = emailBody || `Benvolguts,\n\nUs adjuntem el pressupost ${budgetNumber}.\n\nQuedo a la vostra disposició per a qualsevol consulta.`;
    window.location.href = `mailto:${emailTo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setEmailModal(false);
  };

  const openDuplicateModal = () => {
    setDuplicateEcOption('same');
    setDuplicateEcId('');
    setDuplicateEcSelected(null);
    setDuplicateEcSearch('');
    setDuplicateEcResults([]);
    setDuplicateModal(true);
  };

  const searchDuplicateEcs = async (query: string) => {
    setDuplicateEcSearch(query);
    if (query.trim().length < 2) { setDuplicateEcResults([]); return; }
    setSearchingDuplicateEc(true);
    try {
      const res = await api.get<{ success: boolean; data: { id: string; ecNumber: string; name: string; etiquetaExpedient?: string | null; client?: { name: string } }[] }>(
        '/expedients-comercials', { search: query, limit: 20 },
      );
      setDuplicateEcResults((res.data ?? []).filter((ec) => ec.id !== form.ecId));
    } catch { setDuplicateEcResults([]); }
    finally { setSearchingDuplicateEc(false); }
  };

  const handleDuplicate = async () => {
    if (!id) return;
    setDuplicating(true);
    try {
      const ecId = duplicateEcOption === 'same' ? (form.ecId || null) : (duplicateEcId || null);
      const res = await api.post<{ success: boolean; data: any }>(`/budgets/${id}/duplicate`, { ecId });
      if (res.success) navigate(`/crm/pressupostos/${res.data.id}/edit`);
    } catch { alert('Error duplicant el pressupost'); }
    finally { setDuplicating(false); }
  };

  // ── "Comanda Client" — documents que el client envia un cop acceptat ────────

  const fetchDocuments = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ success: boolean; data: BudgetDocumentRow[] }>(`/budgets/${id}/documents`);
      setDocuments(res.data ?? []);
    } catch { /* no bloquejar la resta del formulari si falla el llistat */ }
  }, [id]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUploadDocument = async (file: File) => {
    if (!id || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await apiClient.post(`/budgets/${id}/documents`, fd, { headers: { 'Content-Type': undefined } });
      fetchDocuments();
    } catch (err: any) {
      alert('Error pujant el document: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDocsDragOver(false);
    Array.from(e.dataTransfer.files ?? []).forEach((f) => handleUploadDocument(f));
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    if (!confirm('Eliminar aquest document?')) return;
    try {
      await api.delete(`/budgets/${id}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch { alert('Error eliminant el document'); }
  };

  const startEditDocDescription = (doc: BudgetDocumentRow) => {
    setEditingDocId(doc.id);
    setEditingDocDescription(doc.description ?? '');
  };

  const handleSaveDocDescription = async (docId: string) => {
    if (!id) return;
    try {
      await api.patch(`/budgets/${id}/documents/${docId}`, { description: editingDocDescription });
      setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, description: editingDocDescription.trim() || undefined } : d)));
    } catch { alert('Error desant la descripció'); }
    finally { setEditingDocId(null); }
  };

  // Les rutes de documents requereixen el token d'autenticació (Authorization
  // header), que un <a href> o window.open directe no envia — es baixa com a
  // blob (igual que handleDownloadPdf) i s'obre/descarrega des d'aquí.
  const handleOpenDocument = async (doc: BudgetDocumentRow, download: boolean) => {
    try {
      const res = await apiClient.get(`/budgets/${id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Error obrint el document');
    }
  };

  const fmtFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${fmt2(bytes / 1024)} KB`;
    return `${fmt2(bytes / (1024 * 1024))} MB`;
  };

  // ── Totals calculats ─────────────────────────────────────────────────────

  const totals = calcTotals(form.lines, form.descomptePercent, form.ivaPercent);

  // Factor marge i preu hora (pestanya Ràtios)
  const totalHoresP  = totals.totalHoresP;
  const totalHoresF  = totals.totalHoresF;
  const totalHoresM  = totals.totalHoresM;
  const totalHoresO  = totals.totalHoresO;
  const totalHoresE  = totals.totalHoresE;
  const totalHores   = totals.totalHores;
  const totalCostTr  = totalHoresP * form.horaTaller + totalHoresF * form.horaFabricacio + totalHoresM * form.horaMuntatge
                      + totalHoresO * form.horaOficina + totalHoresE * form.horaExtern;
  const totalCostTreball = totalCostTr;
  // Cost Compres = Cost Materials + Cost Serveis + Cost Indirecte, com a Escandalls.
  const totalCostCompres = totals.totalCostMaterials + totals.totalCostServeis + totals.totalCostIndirecte;
  const totalCostTotal   = totalCostCompres + totalCostTreball;
  const factorMarge      = totalCostTotal > 0 ? totals.baseImposable / totalCostTotal : 0;
  // Preu hora = Valor Afegit / Total Hores, mateixa fórmula que a la pestanya
  // d'Escandalls (Valor Afegit = Base Imposable − Cost Compres, NO la Base
  // Imposable sencera, que també inclou el propi cost del treball).
  const valorAfegit      = totals.baseImposable - totalCostCompres;
  const preuHora         = totalHores > 0 ? valorAfegit / totalHores : 0;
  const margeBrut        = totals.baseImposable - totalCostTotal;

  // Gràfic circular Dades Econòmiques (com Escandalls): Cost Materials, Cost del Treball, Marge Brut.
  const pieData = [
    { name: 'Cost Materials',     value: Math.max(totals.totalCostMaterials, 0), color: '#f87171' },
    { name: 'Cost Serveis',       value: Math.max(totals.totalCostServeis, 0),   color: '#fb923c' },
    { name: 'Cost Indirecte',     value: Math.max(totals.totalCostIndirecte, 0), color: '#64748b' },
    { name: 'Cost del Treball',   value: Math.max(totalCostTreball, 0),          color: '#8b5cf6' },
    { name: 'Marge Brut',         value: Math.max(margeBrut, 0),                 color: '#1d4ed8' },
  ].filter((d) => d.value > 0);

  // ─── Renders ─────────────────────────────────────────────────────────────

  if (loading) return <div className="p-8 text-muted-foreground text-xs">Carregant...</div>;

  const inputCls = 'w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';

  const filteredNodes = nodes.filter((n) =>
    !nodeSearch || n.name.toLowerCase().includes(nodeSearch.toLowerCase()) || (n.code ?? '').toLowerCase().includes(nodeSearch.toLowerCase()),
  );

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* ── Header ── */}
      <div className="sticky top-0 z-20 flex-shrink-0">
      <DocumentHeader
        title={isNew ? 'Nou pressupost' : budgetNumber}
        infoBadges={!isNew && (
          <>
            {ecInfo?.brandId && <HeaderBadge tone="brand">{brands.find((b) => b.id === ecInfo.brandId)?.name ?? '—'}</HeaderBadge>}
            {ecInfo && (
              <HeaderLinkBadge tone="expedient" href={`/crm/expedients-comercials/${ecInfo.id}/edit`} title="Obrir Expedient Comercial">
                {ecInfo.ecNumber}
              </HeaderLinkBadge>
            )}
            {acceptedAt && (
              <HeaderBadge tone="success" title="Data d'acceptació">
                Acceptat: {new Date(acceptedAt).toLocaleDateString('ca-ES')}
              </HeaderBadge>
            )}
          </>
        )}
        status={!isNew && (
          <StatusSelect
            value={form.estat}
            options={estatsOpts.map((e) => ({ code: e.code, name: e.name, className: BUDGET_STATUS_CONFIG[e.code]?.className, dot: BUDGET_STATUS_CONFIG[e.code]?.dot }))}
            fallbackLabel={BUDGET_STATUS_CONFIG[form.estat]?.label ?? form.estat}
            onChange={(code) => setForm((f) => ({ ...f, estat: code }))}
          />
        )}
        actions={<>
          {!isNew && (
            <>
              <button onClick={openDuplicateModal}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <Copy size={14} /> Duplicar
              </button>
              <button onClick={handlePrintPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <Printer size={14} /> Vista prèvia
              </button>
              <button onClick={handleDownloadPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <Download size={14} /> PDF
              </button>
              <button onClick={() => {
                setEmailTo(contacts.find((c) => c.id === form.contactId)?.email ?? '');
                setEmailModal(true);
              }}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-muted-foreground hover:bg-gray-50">
                <Mail size={14} /> Enviar
              </button>
            </>
          )}
          <button onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('pressupostos')); navigate('/crm'); }}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
            Cancel·lar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
            <Save size={14} /> {saving ? 'Desant...' : 'Desar'}
          </button>
        </>}
      />

      {/* ── Pestanyes (tarjetes, mateix patro que Escandalls: Dades / Detall) ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <button
            type="button"
            onClick={() => setTab('general')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${tab === 'general' ? 'border-blue-500 bg-blue-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-blue-500/10 shrink-0"><FileText className="h-4 w-4 text-blue-500" /></div>
            <span className="text-xs font-semibold">Dades Generals</span>
          </button>
          <button
            type="button"
            onClick={() => setTab('detall')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${tab === 'detall' ? 'border-teal-500 bg-teal-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-teal-500/10 shrink-0"><Table2 className="h-4 w-4 text-teal-500" /></div>
            <span className="text-xs font-semibold">Detall</span>
          </button>
        </div>
      </div>
      </div>

      {/* ── Contingut ── */}
      <div className="flex-1 overflow-y-auto p-4">

        {/* ──── TAB: Dades Generals ──── */}
        {tab === 'general' && (
          <div className="grid grid-cols-2 gap-6">

            {/* Columna esquerra: Dades Generals */}
            <div className="space-y-6">

              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <FileText size={16} className="text-blue-600" /> Capçalera
                  </h2>
                </div>
                <div className="p-5 space-y-2">
                  {/* Fila 0: Expedient Comercial — nomes quan es crea un pressupost
                      nou sense venir ja preassignat des de la fitxa de l'EC. */}
                  {isNew && !ecInfo && (
                    <div className="relative">
                      <label className={labelCls}>Expedient Comercial</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="Cerca per número, nom o client..."
                        value={ecSearch}
                        onChange={(e) => { searchEcs(e.target.value); setEcOpen(true); }}
                        onFocus={() => setEcOpen(true)}
                        onBlur={() => setTimeout(() => setEcOpen(false), 150)}
                        autoComplete="off"
                      />
                      {searchingEc && <p className="text-[11px] text-muted-foreground mt-1">Cercant...</p>}
                      {ecOpen && ecSearch.trim().length >= 2 && !searchingEc && (
                        <ul className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto text-sm">
                          {ecResults.length === 0 ? (
                            <li className="px-3 py-2 text-xs text-muted-foreground">Sense resultats</li>
                          ) : ecResults.map((ec) => (
                            <li key={ec.id}
                              onMouseDown={(e) => { e.preventDefault(); selectEc(ec); }}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0">
                              <span className="font-medium">{ec.ecNumber} — {ec.etiquetaExpedient || ec.name}</span>
                              {ec.client && <span className="text-gray-400 text-xs ml-2">{ec.client.name}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Opcional — si l&apos;assignes, el client i la marca s&apos;omplen automàticament.
                      </p>
                    </div>
                  )}
                  {/* Fila 1: Data, Client */}
                  <div className="grid grid-cols-[auto_1fr] gap-2 items-start">
                    <div>
                      <label className={labelCls}>Data *</label>
                      <input type="date" className={inputCls} value={form.budgetDate}
                        onChange={(e) => setForm((f) => ({ ...f, budgetDate: e.target.value }))} />
                    </div>
                    <div className="relative">
                      <label className={labelCls}>Client *</label>
                      <div className="flex items-center gap-2">
                        {form.clientId && (
                          <a
                            href={`/crm/clients/${form.clientId}/edit`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Editar fitxa del client"
                            className="text-gray-400 hover:text-blue-600 shrink-0"
                          >
                            <ExternalLink size={15} />
                          </a>
                        )}
                        <div className="relative flex-1">
                          <input
                            type="text"
                            className={inputCls}
                            placeholder="Cerca per nom o CIF..."
                            value={clientSearch}
                            onChange={(e) => { setClientSearch(e.target.value); setClientOpen(true); }}
                            onFocus={() => setClientOpen(true)}
                            onBlur={() => setTimeout(() => setClientOpen(false), 150)}
                            autoComplete="off"
                          />
                      {clientOpen && clientSearch.length >= 1 && (() => {
                        const hits = clients.filter((c) =>
                          c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                          (c.cif ?? '').toLowerCase().includes(clientSearch.toLowerCase())
                        ).slice(0, 10);
                        return hits.length > 0 ? (
                          <ul className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto text-sm">
                            {hits.map((c) => (
                              <li key={c.id}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setForm((f) => ({ ...f, clientId: c.id, contactId: '' }));
                                  setClientSearch(c.name);
                                  setClientOpen(false);
                                  loadContacts(c.id);
                                  loadClientAddresses(c.id);
                                  applyClientIdioma(c);
                                }}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0">
                                <span className="font-medium">{c.name}</span>
                                {c.cif && <span className="text-gray-400 text-xs ml-2">{c.cif}</span>}
                              </li>
                            ))}
                          </ul>
                        ) : null;
                      })()}
                      {form.clientId && !clientOpen && (
                        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                          onClick={() => { setForm((f) => ({ ...f, clientId: '', contactId: '' })); setClientSearch(''); setContacts([]); setClientAddresses([]); }}>
                          ✕
                        </button>
                      )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fila 2: Nom del projecte */}
                  <div>
                    <label className={labelCls}>Nom del projecte</label>
                    <input className={inputCls} value={form.projecte}
                      onChange={(e) => setForm((f) => ({ ...f, projecte: e.target.value }))} />
                  </div>

                  {/* Fila 3: Contacte, La nostra referència */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>Contacte</label>
                      <select className={inputCls} value={form.contactId}
                        onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}
                        disabled={!form.clientId}>
                        <option value="">— Cap contacte —</option>
                        {contacts.map((c) => (
                          <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>La nostra referència</label>
                      <select className={inputCls} value={form.nostraReferencia}
                        onChange={(e) => setForm((f) => ({ ...f, nostraReferencia: e.target.value }))}>
                        <option value="">— Selecciona —</option>
                        {companyUsers.map((u) => (
                          <option key={u.id} value={`${u.firstName} ${u.lastName}`}>{u.firstName} {u.lastName}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Fila 4: Entradeta */}
                  <div>
                    <label className={labelCls}>Entradeta (text introductori per al client)</label>
                    <textarea rows={2} className={inputCls} value={form.entradeta}
                      onChange={(e) => setForm((f) => ({ ...f, entradeta: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* Notes i observacions */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <MessageSquare size={16} className="text-teal-600" /> Notes i observacions
                </h2>
              </div>
              <div className="p-5 grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Notes internes (no apareixen al PDF)</label>
                  <textarea rows={2} className={inputCls} value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Observacions per al client (apareixen al PDF)</label>
                  <textarea rows={2} className={inputCls} value={form.observacions}
                    onChange={(e) => setForm((f) => ({ ...f, observacions: e.target.value }))} />
                </div>
              </div>
            </div>

            </div>

            {/* Columna dreta: Ràtios */}
            <div className="space-y-6">
              {/* Entrega */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setEntregaOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {entregaOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Truck size={16} className="text-orange-600" /> Condicions de lliuraments
                  </button>
                </div>
                {entregaOpen && (
                <div className="p-5 grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Lliurament previst</label>
                    <input type="text" className={inputCls} value={form.lliuramentPrevist}
                      onChange={(e) => setForm((f) => ({ ...f, lliuramentPrevist: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Ports</label>
                    <input className={inputCls} value={form.ports}
                      onChange={(e) => setForm((f) => ({ ...f, ports: e.target.value }))} />
                  </div>
                  <div>
                    {clientAddresses.length > 0 && (
                      <select
                        className={`${inputCls} bg-white`}
                        value=""
                        onChange={(e) => {
                          const a = clientAddresses.find((x) => x.id === e.target.value);
                          if (a) {
                            setForm((f) => ({ ...f, llocLliurament: formatClientAddress(a) }));
                            setLlocLliuramentGps(a.gpsCoordinates || '');
                          }
                        }}
                      >
                        <option value="">— Tria una adreça del client —</option>
                        {clientAddresses.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.isPrimary ? '★ ' : ''}{a.label ? `${a.label} — ` : ''}{a.address}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Condicions de lliurament</label>
                    <input className={inputCls} value={form.condicioLliurament}
                      onChange={(e) => setForm((f) => ({ ...f, condicioLliurament: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Lloc de lliurament</label>
                    <textarea rows={2} className={`${inputCls} resize-none`} value={form.llocLliurament}
                      placeholder="Escriu una adreça o tria-la a sobre..."
                      onChange={(e) => { setForm((f) => ({ ...f, llocLliurament: e.target.value })); setLlocLliuramentGps(''); }} />
                    {llocLliuramentGps && (
                      <a
                        href={`https://www.google.com/maps?q=${encodeURIComponent(llocLliuramentGps)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 px-2 py-1 border border-gray-200 rounded text-xs text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      >
                        <MapPin size={12} /> {llocLliuramentGps} — Obrir a Google Maps
                      </a>
                    )}
                  </div>
                </div>
                )}
              </div>

              {/* Pagament + Fiscal */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setPagamentOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {pagamentOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Wallet size={16} className="text-green-600" /> Pagament i fiscal
                  </button>
                </div>
                {pagamentOpen && (
                <div className="p-5 grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Pagament</label>
                    <select className={inputCls} value={form.sistPagament}
                      onChange={(e) => setForm((f) => ({ ...f, sistPagament: e.target.value }))}>
                      <option value="">— Selecciona —</option>
                      {metodesPagament.map((m) => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Venciment (dies)</label>
                    <input type="text" inputMode="numeric" className={inputCls} value={form.venciment}
                      placeholder="Ex: 30"
                      onChange={(e) => setForm((f) => ({ ...f, venciment: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Número de compte</label>
                    {bankAccounts.length > 0 ? (
                      <select className={`${inputCls} font-mono`} value={form.numCompte}
                        onChange={(e) => setForm((f) => ({ ...f, numCompte: e.target.value }))}>
                        <option value="">— Selecciona compte —</option>
                        {bankAccounts.map((acc) => (
                          <option key={acc.id} value={acc.iban}>
                            {acc.iban.replace(/(.{4})/g, '$1 ').trim()}
                            {acc.bankName ? ` — ${acc.bankName}` : ''}
                            {acc.isDefault ? ' ★' : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input className={`${inputCls} font-mono`} value={form.numCompte}
                        onChange={(e) => setForm((f) => ({ ...f, numCompte: e.target.value }))} />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Descompte global (%)</label>
                    <input type="number" min="0" max="100" step="any" className={inputCls}
                      value={form.descomptePercent}
                      onChange={(e) => setForm((f) => ({ ...f, descomptePercent: parseFloat(e.target.value) || 0 }))} />
                  </div>
                  <div>
                    <label className={labelCls}>IVA (%)</label>
                    <input type="number" min="0" max="100" step="0.01" className={inputCls}
                      value={form.ivaPercent}
                      onChange={(e) => setForm((f) => ({ ...f, ivaPercent: parseFloat(e.target.value) || 0 }))} />
                  </div>
                </div>
                )}
              </div>

              {/* Ràtios */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setRatisOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {ratisOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <BarChart3 size={16} className="text-purple-600" /> Ratis
                  </button>
                </div>
                {ratisOpen && (
                <div className="p-5 space-y-1 text-xs">
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Factor Marge (BI/Cost)</span>
                    <span className="flex items-baseline shrink-0">
                      <span className={`w-14 text-right font-bold tabular-nums ${factorMarge < 1.5 ? 'text-amber-600' : 'text-green-600'}`}>{fmt2(factorMarge)}</span>
                      <span className="w-10 text-left text-muted-foreground font-normal">&nbsp;</span>
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Preu hora (BI/H)</span>
                    <span className="flex items-baseline shrink-0">
                      <span className={`w-14 text-right font-bold tabular-nums ${preuHora < 45 ? 'text-amber-600' : 'text-foreground'}`}>{fmt2(preuHora)}</span>
                      <span className="w-10 text-left text-muted-foreground font-normal">&nbsp;€/h</span>
                    </span>
                  </div>
                  <div className="border-t border-gray-100 mt-1 pt-1" />
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground font-medium">Total hores</span>
                    <span className="flex items-baseline shrink-0">
                      <span className="w-14 text-right font-medium tabular-nums">{fmt2(totalHores)}</span>
                      <span className="w-10 text-left text-muted-foreground font-normal">&nbsp;h</span>
                    </span>
                  </div>
                  {form.amidament > 0 && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-muted-foreground">Hores/UT (Total hores/Amidament)</span>
                      <span className="flex items-baseline shrink-0">
                        <span className="w-14 text-right tabular-nums">{fmt2(totalHores / form.amidament)}</span>
                        <span className="w-10 text-left text-muted-foreground">&nbsp;h/{form.unitatAmidament}</span>
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Total pes</span>
                    <span className="flex items-baseline shrink-0">
                      <span className="w-14 text-right tabular-nums">{fmt2(totals.totalPes)}</span>
                      <span className="w-10 text-left text-muted-foreground">&nbsp;kg</span>
                    </span>
                  </div>
                  {totals.totalPes > 0 && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-muted-foreground">BI / Pes</span>
                      <span className="flex items-baseline shrink-0">
                        <span className="w-14 text-right tabular-nums">{fmt2(totals.baseImposable / totals.totalPes)}</span>
                        <span className="w-10 text-left text-muted-foreground">&nbsp;€/kg</span>
                      </span>
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Dades econòmiques */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setDadesEconomiquesOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {dadesEconomiquesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Calculator size={16} className="text-blue-600" /> Dades econòmiques
                  </button>
                </div>
                {dadesEconomiquesOpen && (
                <div className="p-5 space-y-1 text-xs">
                  <div className="flex items-center justify-between gap-2 pb-2 mb-1 border-b border-gray-100">
                    <span className="text-muted-foreground">Amidament</span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text" inputMode="decimal"
                        className="w-20 px-2 py-1 border border-gray-200 rounded text-xs text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={editCells['amidament'] !== undefined ? editCells['amidament'] : (form.amidament === 0 ? '' : fmt2(form.amidament))}
                        onFocus={() => setEditCells((p) => ({ ...p, amidament: form.amidament === 0 ? '' : String(form.amidament).replace('.', ',') }))}
                        onChange={(e) => setEditCells((p) => ({ ...p, amidament: e.target.value }))}
                        onBlur={() => {
                          const parsed = parseFloat((editCells['amidament'] ?? '').replace(',', '.')) || 0;
                          setForm((f) => ({ ...f, amidament: parsed }));
                          setEditCells((p) => { const n = { ...p }; delete n.amidament; return n; });
                        }}
                      />
                      <select
                        className="w-20 px-1.5 py-1 border border-gray-200 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={form.unitatAmidament}
                        onChange={(e) => setForm((f) => ({ ...f, unitatAmidament: e.target.value }))}
                      >
                        {unitTypes.length > 0
                          ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                          : <option value="Ut">Ut</option>}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-between text-muted-foreground pb-1 mb-1 border-b border-gray-100">
                    <span>&nbsp;</span>
                    <div className="flex gap-3">
                      <span className="w-14 text-right">Hores</span>
                      <span className="w-16 text-right">Preu/h</span>
                      <span className="w-24 text-right">Import</span>
                      <span className="w-20 text-right">Valors/{form.unitatAmidament || 'Ut'}</span>
                      <span className="w-14 text-right">%</span>
                    </div>
                  </div>
                  {[
                    { label: 'Base Imposable (BI)', value: totals.baseImposable, bold: true },
                    { label: `IVA (${form.ivaPercent}%)`, value: totals.ivaImport },
                    { label: 'Total', value: totals.total, bold: true },
                    null,
                    { label: 'Cost Materials', value: totals.totalCostMaterials, hideIfZero: true },
                    { label: 'Cost Serveis', value: totals.totalCostServeis, hideIfZero: true },
                    { label: 'Cost Indirecte', value: totals.totalCostIndirecte, hideIfZero: true },
                    { label: 'Cost H.Oficina T.', value: totalHoresO * form.horaOficina, hours: totalHoresO, rate: form.horaOficina, hideIfZero: true },
                    { label: 'Cost H.Fabricació', value: totalHoresF * form.horaFabricacio, hours: totalHoresF, rate: form.horaFabricacio, hideIfZero: true },
                    { label: 'Cost H.Muntatge', value: totalHoresP * form.horaTaller, hours: totalHoresP, rate: form.horaTaller, hideIfZero: true },
                    { label: 'Cost H.Muntatge', value: totalHoresM * form.horaMuntatge, hours: totalHoresM, rate: form.horaMuntatge, hideIfZero: true },
                    { label: 'Cost H.Extern', value: totalHoresE * form.horaExtern, hours: totalHoresE, rate: form.horaExtern, hideIfZero: true },
                    { label: 'Total Cost Treball', value: totalCostTr, bold: true },
                    { label: 'Cost Total', value: totalCostTotal, bold: true },
                    { label: 'Marge Brut', value: margeBrut, bold: true },
                    { label: 'Valor Afegit', value: valorAfegit, bold: true },
                  ].filter((row) => !row || !row.hideIfZero || Math.abs(row.value) > 0.005).map((row, i) => {
                    if (!row) return <div key={i} className="border-t border-gray-100 my-1" />;
                    const pct = totals.baseImposable !== 0 ? (row.value / totals.baseImposable) * 100 : 0;
                    const ua  = form.amidament > 0 ? row.value / form.amidament : null;
                    return (
                      <div key={i} className={`flex justify-between ${row.bold ? 'font-semibold border-t border-gray-200 pt-1 mt-1' : 'text-muted-foreground'}`}>
                        <span className="truncate mr-2">{row.label}</span>
                        <div className="flex gap-3 shrink-0">
                          <span className="w-14 text-right whitespace-nowrap text-muted-foreground">{row.hours != null ? `${fmt2(row.hours)} h` : ''}</span>
                          <span className="w-16 text-right whitespace-nowrap text-muted-foreground">{row.rate != null ? `${fmt2(row.rate)} €` : ''}</span>
                          <span className={`w-24 text-right whitespace-nowrap ${row.value < 0 ? 'text-red-600' : ''}`}>{fmt2(row.value)} €</span>
                          <span className="w-20 text-right whitespace-nowrap text-muted-foreground">{ua != null ? `${fmt2(ua)} €` : '—'}</span>
                          <span className="w-14 text-right whitespace-nowrap text-muted-foreground">{fmt2(pct)} %</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>

              {/* Gràfic Dades Econòmiques (com Escandalls) */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setGraficOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {graficOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <PieChartIcon size={16} className="text-teal-600" /> Gràfic Dades Econòmiques
                  </button>
                </div>
                {graficOpen && (
                <div className="p-5">
                {pieData.length > 0 ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-full">
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart margin={{ top: 24, right: 24, bottom: 24, left: 24 }}>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={85}
                            paddingAngle={2}
                            animationBegin={0}
                            animationDuration={700}
                            label={({ percent }: { percent: number }) => (percent >= 0.04 ? `${fmt2(percent * 100)} %` : '')}
                            labelLine={false}
                          >
                            {pieData.map((d) => <Cell key={d.name} fill={d.color} stroke="none" />)}
                          </Pie>
                          <Tooltip formatter={(v: number, _n: string, entry: any) => [`${fmt2(v)} € (${fmt2((entry?.payload?.percent ?? 0) * 100)} %)`, entry?.payload?.name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="w-full space-y-1">
                      {(() => {
                        const pieTotal = pieData.reduce((s, d) => s + d.value, 0);
                        return pieData.map((d) => (
                          <div key={d.name} className="flex items-center gap-1.5 text-xs">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                            <span className="flex-1 min-w-0 truncate text-muted-foreground" title={d.name}>{d.name}</span>
                            <span className="tabular-nums shrink-0 text-muted-foreground">{pieTotal > 0 ? fmt2(d.value / pieTotal * 100) : '0,00'} %</span>
                            <span className="tabular-nums shrink-0">{fmt2(d.value)} €</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-xs text-center">Sense dades per representar</p>
                )}
                </div>
                )}
              </div>

              {/* Comanda Client — documents que el client envia un cop acceptat el pressupost */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setDocumentsOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {documentsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Paperclip size={16} className="text-amber-600" /> Documents Expedient Comercial
                    {documents.length > 0 && (
                      <span className="text-xs font-normal text-muted-foreground">({documents.length})</span>
                    )}
                  </button>
                </div>
                {documentsOpen && (
                <div className="p-5">
                  {isNew ? (
                    <p className="text-muted-foreground text-xs text-center py-4">Desa el pressupost abans de pujar documents.</p>
                  ) : (
                    <>
                      <label
                        onDragOver={(e) => { e.preventDefault(); setDocsDragOver(true); }}
                        onDragLeave={() => setDocsDragOver(false)}
                        onDrop={handleDocumentsDrop}
                        className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg py-4 cursor-pointer transition-colors ${
                          docsDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <Upload size={18} className="text-gray-400" />
                        <span className="text-xs text-muted-foreground">
                          {uploadingDoc ? 'Pujant...' : 'Arrossega o clica per pujar un document'}
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          disabled={uploadingDoc}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUploadDocument(file);
                            e.target.value = '';
                          }}
                        />
                      </label>

                      {documents.length === 0 ? (
                        <p className="text-muted-foreground text-xs text-center py-3">Encara no hi ha cap document</p>
                      ) : (
                        <div className="mt-3 space-y-1">
                          {documents.map((doc) => (
                            <div key={doc.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 text-xs">
                              <FileText size={14} className="text-gray-400 shrink-0" />
                              <div className="w-80 shrink-0 min-w-0">
                                <div className="truncate font-medium" title={doc.name}>{doc.name}</div>
                                <div className="text-muted-foreground">{fmtFileSize(doc.fileSize)} · {new Date(doc.createdAt).toLocaleDateString('ca-ES')}</div>
                              </div>
                              <div className="flex-1 min-w-0 flex items-center">
                                {editingDocId === doc.id ? (
                                  <input
                                    type="text"
                                    autoFocus
                                    value={editingDocDescription}
                                    onChange={(e) => setEditingDocDescription(e.target.value)}
                                    onBlur={() => handleSaveDocDescription(doc.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveDocDescription(doc.id);
                                      if (e.key === 'Escape') setEditingDocId(null);
                                    }}
                                    placeholder="Descripció del document"
                                    className="w-full px-1.5 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-xs"
                                  />
                                ) : doc.description ? (
                                  <div className="truncate text-muted-foreground" title={doc.description}>{doc.description}</div>
                                ) : null}
                              </div>
                              <button type="button" title="Editar descripció" onClick={() => startEditDocDescription(doc)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-amber-600 transition-colors">
                                <Pencil size={14} />
                              </button>
                              <button type="button" title="Visualitzar" onClick={() => handleOpenDocument(doc, false)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                                <Eye size={14} />
                              </button>
                              <button type="button" title="Descarregar" onClick={() => handleOpenDocument(doc, true)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors">
                                <Download size={14} />
                              </button>
                              <button type="button" title="Eliminar" onClick={() => handleDeleteDocument(doc.id)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ──── TAB: Detall ──── */}
        {tab === 'detall' && (
          <div className="space-y-3">
            <div className="bg-muted/30 border rounded">
              <div className="w-full bg-slate-100 px-4 py-2 font-medium flex justify-between items-center gap-3 text-[11px] rounded-t">
                <span className="shrink-0">Detall</span>
                <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
                  <Button size="sm" variant="outline" className="text-[11px]" onClick={() => exportBudgetDetallToExcel(`${sanitizeFilename(budgetNumber || 'Pressupost')} — Detall.xls`, form.lines)}>
                    <Download className="h-4 w-4 mr-1" /> Excel
                  </Button>
                  <Button size="sm" variant="outline" className="text-[11px]" onClick={() => printBudgetDetallTable(`${budgetNumber || 'Pressupost'} — Detall`, form.lines)}>
                    <Printer className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" className="text-[11px]" onClick={addLine}>
                    <Plus className="h-4 w-4 mr-1" /> Afegir línia
                  </Button>
                </div>
              </div>

            <div className="overflow-x-auto">
              <table className="escandall-lines-table w-full" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                <thead className="bg-slate-200">
                  <tr>
                    {[
                      { h: '#', align: 'text-left' },
                      { h: 'Qt', align: 'text-right' },
                      { h: 'Ut', align: 'text-left' },
                      { h: 'Concepte', align: 'text-left' },
                      { h: 'Preu/U', align: 'text-right' },
                      { h: 'Dte%', align: 'text-right' },
                      { h: 'Import', align: 'text-right' },
                      { h: '', align: 'text-left' },
                    ].map((c, i) => (
                      <th key={i} className={`px-1 py-0.5 ${c.align} font-medium whitespace-nowrap`}>{c.h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, ri) => {
                    const isExpanded = expandedLines.has(ri);
                    // Totals del desglose local, categoritzats per Tipus (com
                    // Cost Materials/Serveis/Indirecte de la capçalera), per
                    // mostrar-los sota la taula de detall de cada línia.
                    // escandallLines representa 1 unitat de l'escandall — cal
                    // multiplicar per Medició × Amidament (igual que calcTotals)
                    // perque reflecteixi tota la línia (ex. Qt=40) i no nomes 1.
                    const lineQty = (Number(l.medicio) || 0) * (Number(l.amidament) || 1);
                    const detailTotals = (l.escandallLines ?? []).reduce((acc, nl) => {
                      const measurement = calcNodeDetailMeasurement(nl) * lineQty;
                      const costTotal = measurement * Number(nl.resolvedUnitCost || 0);
                      if (nl.childType === 'NODE' || nl.nature === 'MATERIAL') acc.materials += costTotal;
                      else if (nl.nature === 'SERVEIS') acc.serveis += costTotal;
                      else if (nl.nature === 'ALTRES') acc.altres += costTotal;
                      else if (nl.nature === 'MO_PROPIA' || nl.nature === 'MO_SUBCONTRACT') acc.hores += measurement;
                      return acc;
                    }, { materials: 0, serveis: 0, altres: 0, hores: 0 });

                    return (
                      <Fragment key={ri}>
                        <tr
                          draggable
                          onDragStart={() => handleLineDragStart(ri)}
                          onDragEnd={handleLineDragEnd}
                          onDragOver={(e) => { e.preventDefault(); setDropLineTarget(ri); }}
                          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node)) setDropLineTarget(null); }}
                          onDrop={() => handleLineDrop(ri)}
                          className={[
                            ri % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                            dragLineFrom === ri ? 'opacity-40' : '',
                            dropLineTarget === ri ? 'border-t-2 border-teal-400' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <td className="px-1 py-0.5 text-muted-foreground" style={{ fontSize: '9px' }}>
                            <span className="inline-flex items-center gap-1 cursor-grab" title="Arrossega per reordenar">
                              <GripVertical size={12} className="text-gray-500 hover:text-gray-800" />
                              {ri + 1}
                            </span>
                          </td>
                          <td className="px-1 py-0.5 w-16">{numCellInput(ri, 'medicio')}</td>
                          <td className="px-1 py-0.5 w-24">
                            <select value={l.tipus} onChange={(e) => updateLine(ri, 'tipus', e.target.value)}
                              className="w-full h-5 border rounded bg-white" style={{ fontSize: '9px', padding: '0 2px' }}>
                              {l.tipus && !unitTypes.some((ut) => ut.code === l.tipus) && (
                                <option value={l.tipus}>{l.tipus}</option>
                              )}
                              {unitTypes.length > 0
                                ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                                : <option value="Ut">Ut</option>}
                            </select>
                          </td>
                          <td className="px-1 py-0.5 min-w-[160px] relative">
                            <div className="flex items-center gap-0.5">
                              <button
                                type="button"
                                title={isExpanded ? 'Amagar detall' : 'Mostrar detall'}
                                onMouseDown={(e) => { e.preventDefault(); toggleExpandLine(ri, l.nodeId); }}
                                className="shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                              >
                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </button>
                              <Input
                                value={l.concepte}
                                onChange={(e) => updateLine(ri, 'concepte', e.target.value)}
                                placeholder="Escriure text lliure..."
                                className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }}
                              />
                              <button
                                type="button"
                                title="Vincular Escandall"
                                onMouseDown={(e) => { e.preventDefault(); setNodePopupRow(ri); setNodeSearch(''); }}
                                className="shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                              >
                                ⬡
                              </button>
                              {(l.escandallLines ?? []).length > 0 && (
                                <div className="flex items-center gap-2 shrink-0 whitespace-nowrap text-muted-foreground pl-1 border-l border-gray-200" style={{ fontSize: '9px' }}>
                                  <span>Hores <b className="text-foreground">{fmt2(detailTotals.hores)}h</b></span>
                                  <span>Mat. <b className="text-foreground">{fmt2(detailTotals.materials)}€</b></span>
                                  <span>Serv. <b className="text-foreground">{fmt2(detailTotals.serveis)}€</b></span>
                                  <span>Altres <b className="text-foreground">{fmt2(detailTotals.altres)}€</b></span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-1 py-0.5 w-20">{numCellInput(ri, 'preuUA')}</td>
                          <td className="px-1 py-0.5 w-14">{numCellInput(ri, 'descompte')}</td>
                          <td className="px-1 py-0.5 w-20 text-right font-medium text-foreground" style={{ fontSize: '9px' }}>{fmt2(l.import)} €</td>
                          <td className="px-1 py-0.5 w-10">
                            <div className="flex gap-1">
                              <button onClick={() => removeLine(ri)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-slate-50/70 border-t border-dashed border-gray-300">
                            <td colSpan={8} className="p-0">
                              <div className="px-2 py-1.5">
                                {nodeDetailLoading.has(ri) ? (
                                  <div className="text-muted-foreground py-1" style={{ fontSize: '9px' }}>Carregant detall de l'escandall...</div>
                                ) : (
                                  <>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-muted-foreground" style={{ fontSize: '9px' }}>
                                        {l.nodeId
                                          ? "Detall de l'escandall (còpia editable — no modifica l'escandall original)"
                                          : 'Detall de la línia — afegeix-hi línies per configurar el preu a partir del desglose'}
                                      </span>
                                      <div className="flex items-center gap-1">
                                        {l.nodeId && (
                                          <Button size="sm" variant="outline" className="text-[10px] h-5"
                                            title="Torna a carregar el desglose des de l'escandall vinculat (ex: per obtenir les miniatures d'imatge tècnica afegides recentment) — sobreescriu els canvis locals d'aquesta línia"
                                            onClick={() => {
                                              const node = nodes.find((n) => n.id === l.nodeId);
                                              if (!node) { alert("No s'ha trobat l'escandall vinculat"); return; }
                                              if (!confirm("Es tornarà a carregar el desglose des de l'escandall original, sobreescrivint els canvis fets nomes en aquesta línia. Continuar?")) return;
                                              importNode(ri, node);
                                            }}>
                                            Actualitzar desglose
                                          </Button>
                                        )}
                                        <Button size="sm" variant="outline" className="text-[10px] h-5" onClick={() => addDetailLine(ri)}>
                                          <Plus className="h-3 w-3 mr-1" /> Afegir línia
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full" style={{ tableLayout: 'auto', fontSize: '9px', lineHeight: '1.2' }}>
                                        <thead>
                                          <tr className="text-muted-foreground bg-slate-200">
                                            {[
                                              { h: '', align: 'text-left' },
                                              { h: '#', align: 'text-left' },
                                              { h: 'Cap.', align: 'text-left' },
                                              { h: 'Ref. Plànol', align: 'text-left' },
                                              { h: 'Descripció', align: 'text-left' },
                                              { h: 'Operació', align: 'text-left' },
                                              { h: '', align: 'text-center', width: '22px' },
                                              { h: 'Element', align: 'text-left' },
                                              { h: 'Tipus', align: 'text-center' },
                                              { h: 'UA', align: 'text-center' },
                                              { h: 'UN', align: 'text-right' },
                                              { h: 'A', align: 'text-right' },
                                              { h: 'B', align: 'text-right' },
                                              { h: 'C', align: 'text-right' },
                                              { h: 'Amidament', align: 'text-right' },
                                              { h: 'Bloq.', align: 'text-center' },
                                              { h: 'Cost/U', align: 'text-right' },
                                              { h: 'Cost total', align: 'text-right' },
                                              { h: 'Marge', align: 'text-right' },
                                              { h: 'Descompte', align: 'text-right' },
                                              { h: 'Preu', align: 'text-right' },
                                              { h: 'Import', align: 'text-right' },
                                              { h: 'Pes', align: 'text-right' },
                                              { h: '', align: 'text-left' },
                                            ].map((c, ci) => (
                                              <th key={ci} className={`px-1 py-0.5 ${c.align} font-medium whitespace-nowrap`} style={c.width ? { width: c.width, minWidth: c.width } : undefined}>{c.h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(l.escandallLines ?? []).length === 0 && (
                                            <tr>
                                              <td colSpan={24} className="px-2 py-2 text-center text-muted-foreground">
                                                {l.nodeId ? "L'escandall vinculat no te línies de composició" : 'Cap línia de detall — prem "Afegir línia"'}
                                              </td>
                                            </tr>
                                          )}
                                          {groupByChapter(l.escandallLines ?? []).sort((a, b) => {
                                            if (a.chapter === SENSE_CHAPTER) return 1;
                                            if (b.chapter === SENSE_CHAPTER) return -1;
                                            return 0;
                                          }).map((group) => (
                                          <Fragment key={group.chapter}>
                                            {/* Capçalera de capítol — mateix format que el detall de l'escandall (NodeForm.tsx) */}
                                            <tr className="border-t border-slate-300 bg-slate-50">
                                              <td colSpan={24} className="px-2 py-0.5">
                                                <span className="font-semibold text-slate-600" style={{ fontSize: '9px' }}>{group.chapter}</span>
                                              </td>
                                            </tr>
                                          {group.items.map(({ item: nl, originalIdx: ni }) => {
                                            const measurement = calcNodeDetailMeasurement(nl);
                                            const costU = Number(nl.resolvedUnitCost || 0);
                                            const costTotal = measurement * costU;
                                            const marge = Number(nl.marge || 0);
                                            const descompte = Number(nl.descompte || 0);
                                            const preu = costU * marge * (1 - descompte / 100);
                                            const importNl = measurement * preu;
                                            const badge = badgeFor(nl.childType, nl.nature);
                                            return (
                                              <tr
                                                key={ni}
                                                draggable
                                                onDragStart={() => handleDetailDragStart(ri, ni)}
                                                onDragEnd={handleDetailDragEnd}
                                                onDragOver={(e) => { e.preventDefault(); setDropDetailTarget(ni); }}
                                                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node)) setDropDetailTarget(null); }}
                                                onDrop={() => handleDetailDrop(ri, ni)}
                                                className={[
                                                  ni % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                                                  dragDetailFrom?.ri === ri && dragDetailFrom.ni === ni ? 'opacity-40' : '',
                                                  dropDetailTarget === ni ? 'border-t-2 border-teal-400' : '',
                                                ].filter(Boolean).join(' ')}
                                              >
                                                <td className="px-1 py-0.5 text-center cursor-grab text-gray-500 hover:text-gray-800" title="Arrossega per reordenar">
                                                  <GripVertical size={14} className="inline" />
                                                </td>
                                                <td className="px-1 py-0.5 text-muted-foreground">{ni + 1}</td>
                                                <td className="px-1 py-0.5 w-24">
                                                  <Input
                                                    value={capitolDrafts[`${ri}-${ni}`] ?? nl.chapter ?? ''}
                                                    onChange={(e) => setCapitolDrafts((p) => ({ ...p, [`${ri}-${ni}`]: e.target.value }))}
                                                    onBlur={(e) => {
                                                      updateDetailLine(ri, ni, 'chapter', e.target.value);
                                                      setCapitolDrafts((p) => { const n = { ...p }; delete n[`${ri}-${ni}`]; return n; });
                                                    }}
                                                    className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} />
                                                </td>
                                                <td className="px-1 py-0.5 w-20">
                                                  <Input value={nl.referencia} onChange={(e) => updateDetailLine(ri, ni, 'referencia', e.target.value)}
                                                    className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} />
                                                </td>
                                                <td className="px-1 py-0.5 min-w-[160px]">
                                                  <Input value={nl.detail} onChange={(e) => updateDetailLine(ri, ni, 'detail', e.target.value)}
                                                    className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} />
                                                </td>
                                                <td className="px-1 py-0.5 min-w-[160px]">
                                                  <Input value={nl.observations} onChange={(e) => updateDetailLine(ri, ni, 'observations', e.target.value)}
                                                    className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} placeholder="Operació…" />
                                                </td>
                                                <td className="px-0 py-0 text-center" style={{ width: '22px', minWidth: '22px' }}>
                                                  {nl.elementImageUrl && (
                                                    <img
                                                      src={nl.elementImageUrl}
                                                      alt=""
                                                      onClick={() => setZoomImageUrl(nl.elementImageUrl!)}
                                                      style={{ width: '18px', height: '18px', objectFit: 'cover', borderRadius: '2px', display: 'inline-block', cursor: 'zoom-in' }}
                                                    />
                                                  )}
                                                </td>
                                                <td className="px-1 py-0.5 min-w-[110px]">
                                                  <Input
                                                    value={nl.description}
                                                    onChange={(e) => {
                                                      updateDetailLine(ri, ni, 'description', e.target.value);
                                                      setDetailDropdownRect(e.target.getBoundingClientRect());
                                                      searchDetailItems(e.target.value, ri, ni);
                                                    }}
                                                    onBlur={() => setTimeout(clearDetailAutocomplete, 200)}
                                                    placeholder="Cercar material o escandall..."
                                                    className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }} />
                                                </td>
                                                <td className="px-1 py-0.5 text-center">
                                                  {nl.childType === 'NODE' ? (
                                                    <span className={`inline-block font-bold px-1 rounded ${badge.cls}`}>{badge.text}</span>
                                                  ) : (
                                                    <select value={nl.nature} onChange={(e) => updateDetailLine(ri, ni, 'nature', e.target.value)}
                                                      className={`font-bold px-0.5 rounded border-0 outline-none appearance-none ${badge.cls}`}
                                                      style={{ fontSize: '9px', padding: '0 2px', height: '14px' }}>
                                                      <option value="MATERIAL">MT</option>
                                                      <option value="MO_PROPIA">MO</option>
                                                      <option value="MO_SUBCONTRACT">MO</option>
                                                      <option value="SERVEIS">SV</option>
                                                      <option value="ALTRES">AL</option>
                                                    </select>
                                                  )}
                                                </td>
                                                <td className="px-1 py-0.5 w-16">
                                                  <select value={nl.unit} onChange={(e) => updateDetailLine(ri, ni, 'unit', e.target.value)}
                                                    className="w-full h-5 border rounded bg-white" style={{ fontSize: '9px', padding: '0 2px' }}>
                                                    {unitTypes.length > 0
                                                      ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                                                      : <option value="Ut">Ut</option>}
                                                  </select>
                                                </td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'quantity')}</td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'dimensionA', 3)}</td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'dimensionB', 3)}</td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'dimensionH', 3)}</td>
                                                <td className="px-1 py-0.5 w-16 text-right text-muted-foreground">{fmt3(measurement)}</td>
                                                <td className="px-1 py-0.5 text-center">
                                                  <input type="checkbox" checked={!!nl.isPriceLocked}
                                                    onChange={(e) => updateDetailLine(ri, ni, 'isPriceLocked', e.target.checked)} />
                                                </td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'resolvedUnitCost')}</td>
                                                <td className="px-1 py-0.5 w-16 text-right text-muted-foreground">{fmt2(costTotal)}</td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'marge')}</td>
                                                <td className="px-1 py-0.5 w-14">{detailNumCellInput(ri, ni, 'descompte')}</td>
                                                <td className="px-1 py-0.5 w-16 text-right text-muted-foreground">{fmt2(preu)}</td>
                                                <td className="px-1 py-0.5 w-16 text-right font-medium">{fmt2(importNl)}</td>
                                                <td className="px-1 py-0.5 w-16">{detailNumCellInput(ri, ni, 'pesTotal')}</td>
                                                <td className="px-1 py-0.5">
                                                  <button onClick={() => removeDetailLine(ri, ni)}
                                                    className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600">
                                                    <Trash2 size={11} />
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                          {/* Subtotal del capítol — mateix format que el detall de l'escandall (NodeForm.tsx) */}
                                          <tr className="bg-slate-50 border-b border-slate-300" style={{ fontSize: '9px', height: '24px' }}>
                                            <td colSpan={17} className="px-1 py-0.5 text-right text-muted-foreground">Subtotal capítol:</td>
                                            <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                              {fmt2(group.items.reduce((s, { item: nl }) => s + calcNodeDetailMeasurement(nl) * Number(nl.resolvedUnitCost || 0), 0))}
                                            </td>
                                            <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                              {(() => {
                                                const informed = group.items.map(({ item: nl }) => Number(nl.marge || 0)).filter((m) => m > 0);
                                                return informed.length > 0 ? fmt2(informed.reduce((s, m) => s + m, 0) / informed.length) : '';
                                              })()}
                                            </td>
                                            <td />
                                            <td />
                                            <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                              {fmt2(group.items.reduce((s, { item: nl }) => {
                                                const measurement = calcNodeDetailMeasurement(nl);
                                                const costU = Number(nl.resolvedUnitCost || 0);
                                                const preu = costU * Number(nl.marge || 0) * (1 - Number(nl.descompte || 0) / 100);
                                                return s + measurement * preu;
                                              }, 0))}
                                            </td>
                                            <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                              {fmt2(group.items.reduce((s, { item: nl }) => s + Number(nl.pesTotal || 0), 0))} Kg
                                            </td>
                                            <td />
                                          </tr>
                                          </Fragment>
                                          ))}
                                        </tbody>
                                        {(l.escandallLines ?? []).length > 0 && (
                                          <tfoot className="bg-slate-100 font-medium" style={{ fontSize: '9px' }}>
                                            <tr style={{ height: '24px' }}>
                                              <td colSpan={17} className="px-1 py-0.5 text-right">Totals:</td>
                                              <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                                {fmt2((l.escandallLines ?? []).reduce((s, nl) => s + calcNodeDetailMeasurement(nl) * Number(nl.resolvedUnitCost || 0), 0))}
                                              </td>
                                              <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                                {(() => {
                                                  const informed = (l.escandallLines ?? []).map((nl) => Number(nl.marge || 0)).filter((m) => m > 0);
                                                  return informed.length > 0 ? fmt2(informed.reduce((s, m) => s + m, 0) / informed.length) : '';
                                                })()}
                                              </td>
                                              <td />
                                              <td />
                                              <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                                {fmt2((l.escandallLines ?? []).reduce((s, nl) => {
                                                  const measurement = calcNodeDetailMeasurement(nl);
                                                  const costU = Number(nl.resolvedUnitCost || 0);
                                                  const preu = costU * Number(nl.marge || 0) * (1 - Number(nl.descompte || 0) / 100);
                                                  return s + measurement * preu;
                                                }, 0))}
                                              </td>
                                              <td className="px-1 py-0.5 text-right whitespace-nowrap">
                                                {fmt2((l.escandallLines ?? []).reduce((s, nl) => s + Number(nl.pesTotal || 0), 0))} Kg
                                              </td>
                                              <td />
                                            </tr>
                                          </tfoot>
                                        )}
                                      </table>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-medium text-xs border-t-2 border-gray-300">
                    <td colSpan={6} className="px-2 py-1.5">TOTALS</td>
                    <td className="px-2 py-1.5 text-right font-bold">{fmt2(totals.subtotal)} €</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </div>

            {/* Totals */}
            <div className="flex items-center justify-end gap-6">
              <div className="bg-muted/30 rounded-lg border border-gray-200 px-4 py-2 flex items-center gap-6 text-xs">
                {form.descomptePercent > 0 && (
                  <>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>Subtotal</span><span className="font-medium text-foreground">{fmt2(totals.subtotal)} €</span>
                    </div>
                    <div className="flex items-center gap-2 text-orange-600">
                      <span>Dte. ({form.descomptePercent}%)</span><span>- {fmt2(totals.descompteImport)} €</span>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="font-bold text-xs text-foreground">Import</span>
                  <span className="font-bold text-xs text-blue-600">{fmt2(totals.baseImposable)} €</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Autocomplete d'"Element" al desglose local (portal) ── */}
      {activeDetailSearch !== null && detailDropdownRect && createPortal(
        <div
          style={{
            position: 'fixed',
            ...(detailDropdownRect.bottom + 220 > window.innerHeight
              ? { bottom: window.innerHeight - detailDropdownRect.top + 2 }
              : { top: detailDropdownRect.bottom + 2 }),
            left: detailDropdownRect.left,
            minWidth: detailDropdownRect.width,
            width: 'max-content',
            maxWidth: 440,
            zIndex: 9999,
          }}
          className="bg-white border rounded-md shadow-xl max-h-52 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {searchingDetail && <div className="px-3 py-2 text-muted-foreground text-xs">Cercant...</div>}
          {!searchingDetail && materialSuggestions.length === 0 && nodeSuggestions.length === 0 && (
            <div className="px-3 py-2 text-muted-foreground text-xs">Sense resultats</div>
          )}
          {!searchingDetail && materialSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Materials</div>
              {materialSuggestions.map((mat) => (
                <div key={mat.id} className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                  onMouseDown={() => selectDetailMaterial(mat, activeDetailSearch.ri, activeDetailSearch.ni)}>
                  <div className="font-medium text-xs whitespace-normal">{mat.name}</div>
                  <div className="text-muted-foreground text-xs whitespace-normal">
                    {mat.code} | {mat.unit} | {formatDecimal(Number(mat.pricePerMagnitude ?? mat.costPerUnit ?? 0))} €/ut
                  </div>
                </div>
              ))}
            </>
          )}
          {!searchingDetail && nodeSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-slate-50">Escandalls</div>
              {nodeSuggestions.map((node) => (
                <div key={node.id} className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b last:border-b-0"
                  onMouseDown={() => selectDetailNode(node, activeDetailSearch.ri, activeDetailSearch.ni)}>
                  <div className="font-medium text-xs whitespace-normal">{node.name}</div>
                  <div className="text-muted-foreground text-xs">
                    {node.code ? `${node.code} | ` : ''}Cost: {fmt2(node.cachedTotalCost)} €
                  </div>
                </div>
              ))}
            </>
          )}
        </div>,
        document.body,
      )}

      {/* ── Modal Node Popup ── */}
      {nodePopupRow !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setNodePopupRow(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[480px] max-h-[60vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <h2 className="font-semibold text-foreground text-sm">Vincular Escandall</h2>
              <input autoFocus className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Cercar per nom o codi..."
                value={nodeSearch} onChange={(e) => setNodeSearch(e.target.value)} />
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredNodes.slice(0, 50).map((n) => (
                <button key={n.id} onClick={() => importNode(nodePopupRow, n)}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 text-xs">
                  <span className="font-medium text-foreground">{n.name}</span>
                  {n.code && <span className="ml-2 text-muted-foreground text-xs">{n.code}</span>}
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Cost {fmt2(n.cachedTotalCost)} € · {fmt2(n.cachedTotalHoursOwn + n.cachedTotalHoursSubcontract)} h · {fmt2(n.cachedTotalWeightKg)} kg
                  </div>
                </button>
              ))}
              {filteredNodes.length === 0 && (
                <p className="p-4 text-xs text-muted-foreground text-center">No s'han trobat escandalls</p>
              )}
            </div>
            <div className="p-3 border-t border-gray-100 flex justify-end">
              <button onClick={() => setNodePopupRow(null)} className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-gray-100 rounded">
                Cancel·lar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox imatge tècnica del desglose ── */}
      {zoomImageUrl && createPortal(
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] cursor-zoom-out"
          onClick={() => setZoomImageUrl(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white hover:text-slate-300"
            onClick={() => setZoomImageUrl(null)}
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={zoomImageUrl}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}

      {/* ── Modal Email ── */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEmailModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-[480px]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-foreground">Enviar pressupost per email</h2>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className={labelCls}>Destinatari *</label>
                <input type="email" className={inputCls} value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)} placeholder="email@client.com" />
              </div>
              <div>
                <label className={labelCls}>Cos del missatge (opcional)</label>
                <textarea rows={4} className={inputCls} value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder={`Benvolguts,\n\nUs adjuntem el pressupost ${budgetNumber}.\n\nQuedo a la vostra disposició per a qualsevol consulta.`} />
              </div>
              <p className="text-xs text-gray-400">S'obrirà el teu client de correu; recorda descarregar i adjuntar el PDF manualment.</p>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2 justify-end">
              <button onClick={() => setEmailModal(false)} className="px-3 py-2 text-xs text-muted-foreground hover:bg-gray-100 rounded">
                Cancel·lar
              </button>
              <button onClick={handleSendEmail}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700">
                <Mail size={14} /> Obrir client de correu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Duplicar ── */}
      {duplicateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !duplicating && setDuplicateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-[480px]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-foreground">Duplicar pressupost</h2>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Es crearà una còpia d'aquest pressupost en estat Esborrany, amb totes les línies. Vols mantenir-lo al mateix Expedient Comercial o assignar-lo a un altre?
              </p>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="radio" name="duplicateEc" checked={duplicateEcOption === 'same'}
                  onChange={() => setDuplicateEcOption('same')} />
                Mantenir el mateix Expedient Comercial{ecInfo ? ` (${ecInfo.ecNumber})` : ''}
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="radio" name="duplicateEc" checked={duplicateEcOption === 'other'}
                  onChange={() => setDuplicateEcOption('other')} />
                Assignar a un altre Expedient Comercial (de qualsevol client)
              </label>
              {duplicateEcOption === 'other' && (
                <div>
                  <input
                    type="text"
                    className={inputCls}
                    value={duplicateEcSelected ? `${duplicateEcSelected.ecNumber} — ${duplicateEcSelected.etiquetaExpedient || duplicateEcSelected.name}` : duplicateEcSearch}
                    onChange={(e) => { setDuplicateEcId(''); setDuplicateEcSelected(null); searchDuplicateEcs(e.target.value); }}
                    placeholder="Cercar per número, nom o client..."
                  />
                  {searchingDuplicateEc && <p className="text-[11px] text-muted-foreground mt-1">Cercant...</p>}
                  {!searchingDuplicateEc && duplicateEcSearch.trim().length >= 2 && !duplicateEcSelected && (
                    <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      {duplicateEcResults.length === 0 ? (
                        <p className="px-3 py-2 text-[11px] text-muted-foreground">Sense resultats</p>
                      ) : duplicateEcResults.map((ec) => (
                        <div key={ec.id}
                          className="px-3 py-1.5 text-xs hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                          onClick={() => { setDuplicateEcId(ec.id); setDuplicateEcSelected(ec); setDuplicateEcSearch(''); }}>
                          <div className="font-medium">{ec.ecNumber} — {ec.etiquetaExpedient || ec.name}</div>
                          {ec.client && <div className="text-muted-foreground">{ec.client.name}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {duplicateEcSelected?.client && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Aquest expedient és del client "{duplicateEcSelected.client.name}" — el pressupost duplicat canviarà de client.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2 justify-end">
              <button onClick={() => setDuplicateModal(false)} disabled={duplicating}
                className="px-3 py-2 text-xs text-muted-foreground hover:bg-gray-100 rounded disabled:opacity-40">
                Cancel·lar
              </button>
              <button
                onClick={async () => { await handleDuplicate(); setDuplicateModal(false); }}
                disabled={duplicating || (duplicateEcOption === 'other' && !duplicateEcId)}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                <Copy size={14} /> {duplicating ? 'Duplicant...' : 'Duplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
